import React, { useState, useEffect, useMemo } from 'react';
import * as d3 from 'd3';
import { BetweenVerticalEnd, Ellipsis, AlertTriangle, Timer, GitBranch, Diff, AlertCircle, UserSquare2, Users, ArrowUp, ArrowDown, Calendar, MapPin, MessageSquare, Search, CheckCircle, Sparkles, Link as LinkIcon, Plus, Trash2, Target, Zap } from 'lucide-react';
import { useTheme } from '../../utils/ThemeSwitcher';
import DurationTimelineChart from '../dashboard/charts/DurationTimelineChart';
import './ConfigureDetectors.css'
import AILoadingOverlay from '../common/AILoadingOverlay';

// API Base URL - uses environment variable in production
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';


const ConfigureDetectors = ({ 
  
  data,
  projectId,  // NEW: Project ID for server-side quality checks (Phase 2)
  onNavigate,
  selectedChecks,
  setSelectedChecks,
  selectedVariables,
  setSelectedVariables,
  dataCollectorId,
  setDataCollectorId,
  respondentId, 
  setRespondentId,
  interviewDateId,
  setInterviewDateId,
  groupByVariable,
  setGroupByVariable,
  checkResults,
  setCheckResults,
  isLoadingChecks,
  setIsLoadingChecks,
  checkError,
  setCheckError,
  columnTypes,
  setColumnTypes,
  typeValidationIssues,
  setTypeValidationIssues,
  isExpandedDQC,
  setIsExpandedDQC,
  onBackToModeSelector,
  xlsformData,  // NEW: XLSForm data for skip logic analysis
  // Lifted state for persistence
  textClusteringSettings,
  setTextClusteringSettings,
  sentinelSettings,
  setSentinelSettings,
  gatedFieldsSettings,
  setGatedFieldsSettings,
  probeDepthSettings,
  setProbeDepthSettings,
  straightLiningSettings,
  setStraightLiningSettings,
  durationRanges,
  setDurationRanges,
  setActiveQualityTab,

}) => {

  const [variables, setVariables] = useState([]);
  const [variableOrder, setVariableOrder] = useState({});
  const [hoveredVariable, setHoveredVariable] = useState({ checkId: null, variable: null });
  const [variableSearch, setVariableSearch] = useState({});
  // Cluster boundaries (optional geolocation upgrade): GeoJSON / zipped
  // Shapefile of the lowest cluster level, stored per project.
  const [boundaryStatus, setBoundaryStatus] = useState(null);   // {loaded, groups_count, ...}
  const [boundaryInspect, setBoundaryInspect] = useState(null); // {attributes, sample_values}
  const [boundaryFile, setBoundaryFile] = useState(null);
  const [boundaryAttr, setBoundaryAttr] = useState('');
  const [boundaryBusy, setBoundaryBusy] = useState(false);
  const [boundaryError, setBoundaryError] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    fetch(`${API_BASE_URL}/api/projects/${projectId}/boundaries`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'X-Cloud-Token': localStorage.getItem('token') || '' }
    })
      .then(r => (r.ok ? r.json() : null))
      .then(st => { if (st) setBoundaryStatus(st); })
      .catch(() => {});
  }, [projectId]);
  
  const { isDarkMode } = useTheme();

  // Responsive
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth < 768;

  const DATA_QUALITY_CHECKS = [

    {
      id: 'duplicates',
      name: 'Uniqueness',
      description: 'Identify duplicate entries across selected variables',
      icon: BetweenVerticalEnd,
      allowMultiple: true,
      minVariables: 1,
      maxVariables: 300,  // mirrors server budget (GET /quality-check-budgets)
      allowedTypes: ['all']
    },
    {
      id: 'missingness',
      name: 'Completeness',
      description: 'Analyze missing values in selected variables',
      icon: Ellipsis,
      allowMultiple: true,
      minVariables: 1,
      allowedTypes: ['all']
    },
    {
      id: 'outliers',
      name: 'Outliers',
      description: 'Detect statistical outliers in selected numerical variables',
      icon: AlertTriangle,
      allowMultiple: true,
      maxVariables: 300,  // mirrors server budget (GET /quality-check-budgets)
      minVariables: 1,
      allowedTypes: ['integer', 'float32', 'float64']
    },
    {
        id: 'duration',
        name: 'Duration Analysis',
        description: 'Analyze interview duration patterns over time. Select: (1) pre-calculated duration, (2) start+end timestamps, or (3) duration+date variables.',
        icon: Timer,
        allowMultiple: true,
        minVariables: 1,
        maxVariables: 3,
        allowedTypes: ['timestamp', 'integer', 'float32', 'float64'],
        requiresSpecialHandling: true,
        requiresRangeInput: true
    },
    {
      id: 'survey_flow',
      name: 'Survey Flow',
      description: 'Visualize response flow patterns between survey variables',
      icon: GitBranch,
      allowMultiple: true,
      maxVariables: 75,  // mirrors server budget; Select All still stops at a practical 15
      minVariables: 2,
      allowedTypes: ['all'],
      requiresOrdering: true
    },
    {
      id: 'geolocation',
      name: 'Geolocation',
      description: 'Visualize and analyze interview locations using GPS coordinates',
      icon: MapPin,
      allowMultiple: false,
      minVariables: 2,
      maxVariables: 2,
      allowedTypes: ['float32', 'float64', 'integer'],
      requiresSpecialHandling: true,
      coordinateSelection: true
    },
    {
      id: 'text_clustering',
      name: 'Text Analysis',
      description: 'Cluster and analyze open-ended responses to identify themes, outliers, and similar answers',
      icon: MessageSquare,
      allowMultiple: true,
      minVariables: 1,
      maxVariables: 15,
      allowedTypes: ['string', 'object', 'text'],
      requiresSpecialHandling: true,
      textAnalysis: true
    },
    {
      id: 'sentinel_phrases',
      name: 'Sentinel Phrases',
      description: 'Catch short open-ended responses that should end with a "no other / none" sentinel — typically a sign the enumerator skipped a closing probe',
      icon: Search,
      allowMultiple: true,
      minVariables: 1,
      maxVariables: 40,
      allowedTypes: ['string', 'object', 'text'],
      requiresSpecialHandling: true,
      sentinelPhrases: true
    },
    {
      id: 'gated_fields',
      name: 'Gated-Field Consistency',
      description: 'Pair a "yes/no" gate question with its follow-up field — flag rows where the answers contradict each other',
      icon: LinkIcon,
      allowMultiple: true,
      minVariables: 0,
      maxVariables: 200,
      allowedTypes: ['all'],
      requiresSpecialHandling: true,
      gatedFields: true
    },
    {
      id: 'probe_depth',
      name: 'Probe-Depth Scoring',
      description: 'Score each transcript 0–100 on specificity (noun phrases, domain words, units, generic-filler penalty). Surfaces enumerators who consistently extract thin responses.',
      icon: Target,
      allowMultiple: true,
      minVariables: 1,
      maxVariables: 30,
      allowedTypes: ['string', 'object', 'text'],
      requiresSpecialHandling: true,
      probeDepth: true
    },
    {
      id: 'straight_lining',
      name: 'Straight-Lining',
      description: 'Detect respondents whose answers across Likert / numeric scale columns look flatlined, repetitive, or inattentive. Combines five methods (variance, entropy, repetition, IQR, Isolation Forest) into a per-respondent severity score. Leave variables empty to auto-detect.',
      icon: Zap,
      allowMultiple: true,
      minVariables: 0,         // 0 = auto-detect numeric/Likert columns
      maxVariables: 200,
      allowedTypes: ['integer', 'int', 'int64', 'float32', 'float64', 'float', 'numeric'],
      requiresSpecialHandling: true,
      straightLining: true
    }
  ];
  
  // Add this function in ConfigureDetectors.js
  const getCoordinateVariables = () => {
    if (!variables || !columnTypes) return [];

    // Match only whole words or clear substrings, NOT single letters.
    // Anchored so "y" inside "monthly" or "community" won't false-positive.
    const COORD_REGEX = /(^|_)(lat|latitude|lon|long|lng|longitude|coord|coords|coordinate|coordinates|gps|geo|geolocation|utm|easting|northing)(_|$)/i;
    // Numeric type vocabulary — accept every variant the backend might emit
    const NUMERIC_TYPES = new Set([
      'float32', 'float64', 'float', 'double', 'decimal', 'numeric', 'integer', 'int', 'int64'
    ]);

    return variables.filter(variable => {
      const varType = String(columnTypes[variable] || '').toLowerCase();
      if (!NUMERIC_TYPES.has(varType)) return false;
      return COORD_REGEX.test(variable);
    });
  };


// Function to determine duration configuration based on selected variables
const getDurationConfiguration = (checkId, selectedVars, columnTypes) => {
  if (checkId !== 'duration' || !selectedVars || selectedVars.length === 0) return null;

  const timestampVars = selectedVars.filter(variable => columnTypes[variable] === 'timestamp');
  const numericVars = selectedVars.filter(variable => 
    ['integer', 'float32', 'float64'].includes(columnTypes[variable])
  );

  if (selectedVars.length === 1) {
    if (numericVars.length === 1) {
      return {
        type: 'pre_calculated',
        description: `Using pre-calculated duration variable: ${selectedVars[0]}`
      };
    } else {
      return {
        type: 'invalid',
        description: 'Single variable must be numeric for duration analysis'
      };
    }
  } else if (selectedVars.length === 2) {
    if (timestampVars.length === 2) {
      return {
        type: 'start_end_timestamps',
        description: `Calculate duration from ${timestampVars[0]} to ${timestampVars[1]}`
      };
    } else if (timestampVars.length === 1 && numericVars.length === 1) {
      return {
        type: 'duration_with_date',
        description: `Duration timeline: ${numericVars[0]} over ${timestampVars[0]}`
      };
    } else {
      return {
        type: 'invalid',
        description: 'Two variables must be either: (1) two timestamps, or (2) one duration + one date'
      };
    }
  } else if (selectedVars.length === 3) {
    if (timestampVars.length >= 2) {
      return {
        type: 'start_end_with_date',
        description: `Calculate duration from timestamps with date reference`
      };
    } else {
      return {
        type: 'invalid',
        description: 'Three variables require at least two timestamp variables'
      };
    }
  }

  return {
    type: 'invalid',
    description: 'Invalid variable configuration'
  };
};

  useEffect(() => {
    
    console.log('Initializing data quality checks with data:', data);
    
    if (data) {
      // Handle column types
      if (data.column_types) {
        console.log('Detected column types:', data.column_types);

        // Post-process: upgrade columns with date-like names that backend classified
        // as string/object/integer but whose sample values actually parse as dates.
        // This rescues Stata pipeline dates ("26aug2024") that pandas often leaves as object.
        const DATE_NAME_PATTERN = /(^|_)(date|time|datetime|timestamp|dt|day|submission|submitted|interview|start|end|created|updated|modified|completed|collected)(_|$)/i;
        const STATA_DATE = /^\d{1,2}[\s\-]?(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[\s\-]?\d{2,4}$/i;
        const NUMERIC_DATE = /^\d{1,4}[\/\-]\d{1,2}[\/\-]\d{1,4}(\s+\d{1,2}:\d{2}(:\d{2})?)?$/;
        const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+\-]\d{2}:?\d{2})?$/;
        const TIME_ONLY = /^\d{1,2}:\d{2}(:\d{2}(\.\d+)?)?(\s?[AaPp][Mm])?$/;

        const looksLikeDateValue = (v) => {
          if (v === null || v === undefined || v === '') return false;
          const s = String(v).trim();
          if (!s) return false;
          if (STATA_DATE.test(s) || NUMERIC_DATE.test(s) || ISO_DATETIME.test(s) || TIME_ONLY.test(s)) return true;
          if (/[-/:T]/.test(s) && /\d{4}/.test(s) && !isNaN(Date.parse(s))) return true;
          return false;
        };

        const dateTypes = new Set(['timestamp', 'datetime', 'date', 'datetime64']);
        const patchedTypes = { ...data.column_types };
        let rescuedCount = 0;

        Object.entries(data.column_types).forEach(([col, type]) => {
          if (dateTypes.has(String(type).toLowerCase())) return;
          if (!DATE_NAME_PATTERN.test(col)) return;

          // Sample up to 20 non-null values from this column
          const sampleVals = (data.data || [])
            .slice(0, 50)
            .map(row => row?.[col])
            .filter(v => v !== null && v !== undefined && v !== '');

          if (sampleVals.length === 0) return;
          const dateMatches = sampleVals.filter(looksLikeDateValue).length;
          if (dateMatches / sampleVals.length >= 0.5) {
            console.log(`[type-rescue] Column "${col}" was "${type}" → upgraded to "timestamp" (${dateMatches}/${sampleVals.length} values parse as dates)`);
            patchedTypes[col] = 'timestamp';
            rescuedCount++;
          }
        });

        if (rescuedCount > 0) {
          console.log(`[type-rescue] Rescued ${rescuedCount} date column(s) the backend mislabeled`);
        }
        setColumnTypes(patchedTypes);

        // Log type statistics
        Object.entries(patchedTypes).forEach(([column, type]) => {
          console.log(`Column: ${column}`);
          console.log(`Type: ${type}`);
          console.log(`Sample values:`, data.data.slice(0, 3).map(row => row[column]));
        });
      } else {
        console.warn('No column types provided, inferring types...');
        const inferredTypes = inferColumnTypes(data.data);
        console.log('Inferred column types:', inferredTypes);
        setColumnTypes(inferredTypes);
      }
      
      // Handle validation issues
      if (data.type_validation_issues) {
        console.warn('Type validation issues detected:', data.type_validation_issues);
        setTypeValidationIssues(data.type_validation_issues);
      }
      
      if (data.columns) {
        setVariables(data.columns);
      }
    }
  }, [data, setColumnTypes, setTypeValidationIssues]);

  // Type inference function with robust multi-row voting + Stata date support
  const inferColumnTypes = (sampleData) => {
    if (!sampleData || !sampleData.length) return {};

    // Column names that strongly suggest a date/time column
    const DATE_NAME_PATTERN = /(^|_)(date|time|datetime|timestamp|dt|day|submission|submitted|interview|start|end|created|updated|modified|completed|collected)(_|$)/i;

    // Stata %td default: "26aug2024", "26Aug2024", "26 Aug 2024", "26-Aug-2024"
    const STATA_DATE = /^\d{1,2}[\s\-]?(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[\s\-]?\d{2,4}$/i;
    // Slash/dash numeric dates: "26/08/2024", "2024-08-26", "08-26-2024"
    const NUMERIC_DATE = /^\d{1,4}[\/\-]\d{1,2}[\/\-]\d{1,4}(\s+\d{1,2}:\d{2}(:\d{2})?)?$/;
    // ISO datetime: "2024-08-26T10:30:00", "2024-08-26 10:30:00"
    const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+\-]\d{2}:?\d{2})?$/;
    // Pure time: "16:58", "16:58:00", "4:30 PM"
    const TIME_ONLY = /^\d{1,2}:\d{2}(:\d{2}(\.\d+)?)?(\s?[AaPp][Mm])?$/;

    const isDateLike = (value) => {
      if (value === null || value === undefined) return false;
      const str = String(value).trim();
      if (!str) return false;
      if (STATA_DATE.test(str)) return true;
      if (NUMERIC_DATE.test(str)) return true;
      if (ISO_DATETIME.test(str)) return true;
      if (TIME_ONLY.test(str)) return true;
      // Fallback: Date.parse with guards — must have separators AND a 4-digit year
      if (/[-/:T]/.test(str) && /\d{4}/.test(str) && !isNaN(Date.parse(str))) return true;
      return false;
    };

    const isEmpty = (value) =>
      value === null ||
      value === undefined ||
      value === '' ||
      (typeof value === 'string' && value.trim() === '') ||
      (typeof value === 'number' && isNaN(value));

    // Gather non-empty samples per column
    const stats = {};
    sampleData.forEach(row => {
      Object.entries(row).forEach(([key, value]) => {
        if (!stats[key]) {
          stats[key] = { samples: [], dateLikeName: DATE_NAME_PATTERN.test(key) };
        }
        if (!isEmpty(value)) stats[key].samples.push(value);
      });
    });

    const inferredTypes = {};
    Object.entries(stats).forEach(([key, { samples, dateLikeName }]) => {
      if (samples.length === 0) {
        inferredTypes[key] = 'string';
        return;
      }

      let dateCount = 0, intCount = 0, floatCount = 0;
      samples.forEach(value => {
        if (isDateLike(value)) {
          dateCount++;
          return;
        }
        const str = String(value).trim();
        const num = Number(str);
        if (!isNaN(num) && str !== '') {
          if (Number.isInteger(num)) intCount++;
          else floatCount++;
        }
      });

      const total = samples.length;
      const dateRatio = dateCount / total;
      const intRatio = intCount / total;
      const floatRatio = floatCount / total;

      // Date column name + any date matches → treat as timestamp
      if (dateLikeName && dateRatio >= 0.3) {
        inferredTypes[key] = 'timestamp';
      } else if (dateRatio >= 0.7) {
        inferredTypes[key] = 'timestamp';
      } else if (floatRatio >= 0.5 || (floatCount > 0 && intRatio + floatRatio >= 0.9)) {
        inferredTypes[key] = 'float64';
      } else if (intRatio >= 0.7) {
        inferredTypes[key] = 'integer';
      } else {
        inferredTypes[key] = 'string';
      }
    });

    return inferredTypes;
  };

  // Render type validation warnings if present
  const renderTypeValidationWarnings = () => {
    if (Object.keys(typeValidationIssues).length === 0) return null;

    return (
      <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md"
         style={{
              
            maxHeight: isExpandedDQC ? '500px' : '0px',
            transition: '0.3s ease-in-out',
            paddingBottom: '20px',
            paddingLeft: '2px'
         }}>
        <div className="flex items-center gap-2 mb-2" 
          style={{display: 'flex',}}
          >
          <h3 className="text-yellow-700 font-medium" style={{marginLeft: '0px'}}>Data Type Warnings</h3>
          <AlertCircle 
            className="text-yellow-500" 
            style={{marginTop: '22px', marginLeft: '5px'}} size={20} 
            />
        </div>
        {Object.entries(typeValidationIssues).map(([column, issues]) => (
          <div key={column} className="ml-6 text-sm text-yellow-600">
            <strong>{column}:</strong> {Array.isArray(issues) ? issues.join(', ') : issues}
          </div>
        ))}
      </div>
    );
  };

  // Add logging for when columnTypes changes
  useEffect(() => {
    console.log('columnTypes state updated:', columnTypes);
  }, [columnTypes]);

  // Function to filter variables based on check type
  const getFilteredVariables = (checkId) => {
    const check = DATA_QUALITY_CHECKS.find(c => c.id === checkId);
    if (!check) return variables;

    console.log(`Filtering variables for ${checkId} check`);
    console.log('Available column types:', columnTypes);

    if (check.allowedTypes.includes('all')) {
      return variables;
    }

    // Normalize type strings so the filter accepts every backend variant.
    // Old code paths emit 'decimal'/'text'/'date' while newer ones emit
    // 'float64'/'string'/'timestamp'. Canonicalize here.
    const TYPE_ALIASES = {
      float: 'float64', double: 'float64', decimal: 'float64', numeric: 'float64',
      float32: 'float64',
      int: 'integer', int64: 'integer',
      text: 'string', object: 'string', str: 'string',
      date: 'timestamp', datetime: 'timestamp', datetime64: 'timestamp',
    };
    const canonicalize = (t) => {
      const key = String(t || '').toLowerCase();
      return TYPE_ALIASES[key] || key;
    };

    // Canonicalize the allowedTypes list ONCE, with numeric-group expansion so
    // a check that says ['float64'] also accepts 'integer' where appropriate.
    const allowedCanonical = new Set(check.allowedTypes.map(canonicalize));

    return variables.filter(variable => {
      const rawType = columnTypes[variable];
      const variableType = canonicalize(rawType);
      const isAllowed = allowedCanonical.has(variableType);

      if (!isAllowed) {
        console.log(`${variable} excluded from ${checkId} (raw=${rawType} → canonical=${variableType})`);
      }
      return isAllowed;
    });
  };

  const toggleCheck = (checkId) => {
    setSelectedChecks(prevChecks => {
      const isCurrentlySelected = prevChecks.includes(checkId);
      
      if (isCurrentlySelected) {
        // Remove from selected checks and clear associated data
        setDurationRanges(prev => {
          const newRanges = { ...prev };
          delete newRanges[checkId];
          return newRanges;
        });
        return prevChecks.filter(id => id !== checkId);
      } else {
        // Initialize selectedVariables for this check if not already present
        setSelectedVariables(prev => ({
          ...prev,
          [checkId]: prev[checkId] || []
        }));
        
        // Initialize variableOrder for survey flow if needed
        if (checkId === 'survey_flow') {
          setVariableOrder(prev => ({
            ...prev,
            [checkId]: prev[checkId] || []
          }));
        }
        
        // Initialize duration range for duration check
        if (checkId === 'duration') {
          setDurationRanges(prev => ({
            ...prev,
            [checkId]: { min: '', max: '' }
          }));
        }
        
        return [...prevChecks, checkId];
      }
    });
    
    // Clear results when toggling checks
    setCheckResults(prev => {
      const newResults = { ...prev };
      delete newResults[checkId];
      return newResults;
    });
  };

  // Select every detector card at once. Only touches checks that aren't
  // already selected — mirrors toggleCheck's init side effects per-check,
  // but leaves already-selected checks (and their results) untouched.
  const selectAllChecks = () => {
    const allIds = DATA_QUALITY_CHECKS.map(c => c.id);
    const newlyAdded = allIds.filter(id => !selectedChecks.includes(id));
    if (newlyAdded.length === 0) return;

    setSelectedChecks(allIds);

    setSelectedVariables(prev => {
      const next = { ...prev };
      newlyAdded.forEach(id => { next[id] = next[id] || []; });
      return next;
    });

    if (newlyAdded.includes('survey_flow')) {
      setVariableOrder(prev => ({
        ...prev,
        survey_flow: prev.survey_flow || []
      }));
    }

    if (newlyAdded.includes('duration')) {
      setDurationRanges(prev => ({
        ...prev,
        duration: prev.duration || { min: '', max: '' }
      }));
    }

    // Clear any stale results only for the checks that are newly turned on
    setCheckResults(prev => {
      const next = { ...prev };
      newlyAdded.forEach(id => { delete next[id]; });
      return next;
    });
  };

  // Deselect every detector card at once — mirrors toggleCheck's single-card
  // deselect side effects (drop duration range + cached results) for each
  // card that was selected.
  const deselectAllChecks = () => {
    if (selectedChecks.length === 0) return;
    const toRemove = [...selectedChecks];

    setSelectedChecks([]);

    setDurationRanges(prev => {
      const next = { ...prev };
      toRemove.forEach(id => { delete next[id]; });
      return next;
    });

    setCheckResults(prev => {
      const next = { ...prev };
      toRemove.forEach(id => { delete next[id]; });
      return next;
    });
  };

  const handleVariableSelection = (checkId, e) => {
    const selectedOptions = Array.from(e.target.selectedOptions).map(option => option.value);
    const check = DATA_QUALITY_CHECKS.find(c => c.id === checkId);
    
    if (check.maxVariables && selectedOptions.length > check.maxVariables) {
      alert(`${check.name} supports up to ${check.maxVariables} variables per run (matches the server's compute budget). Deselect some, or run the check in batches.`);
      return;
    }

    setSelectedVariables(prev => ({
      ...prev,
      [checkId]: selectedOptions
    }));

    // Initialize variable order for survey flow
    if (checkId === 'survey_flow' && selectedOptions.length > 0) {
      setVariableOrder(prev => ({
        ...prev,
        [checkId]: selectedOptions
      }));
    }

    // Clear results when changing variables
    setCheckResults(prev => {
      const newResults = { ...prev };
      delete newResults[checkId];
      return newResults;
    });
  };

  // Handle duration range input
  const handleDurationRangeChange = (checkId, field, value) => {
    setDurationRanges(prev => ({
      ...prev,
      [checkId]: {
        ...prev[checkId],
        [field]: value
      }
    }));

    // Clear results when changing range
    setCheckResults(prev => {
      const newResults = { ...prev };
      delete newResults[checkId];
      return newResults;
    });
  };

  // Validate duration range
  const validateDurationRange = (checkId) => {
    const range = durationRanges[checkId];
    if (!range || range.min === '' || range.max === '') {
      return { valid: false, message: 'Both minimum and maximum values are required' };
    }
    
    const min = parseFloat(range.min);
    const max = parseFloat(range.max);
    
    if (isNaN(min) || isNaN(max)) {
      return { valid: false, message: 'Please enter valid numeric values' };
    }
    
    if (min >= max) {
      return { valid: false, message: 'Minimum value must be less than maximum value' };
    }
    
    if (min < 0) {
      return { valid: false, message: 'Minimum value cannot be negative' };
    }
    
    return { valid: true };
  };

  // Select All variables for a check
  const selectAllVariables = (checkId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    const check = DATA_QUALITY_CHECKS.find(c => c.id === checkId);
    if (!check) return;
    
    const filteredVars = getFilteredVariables(checkId);
    
    // For geolocation, don't use select all (it has special coordinate UI)
    if (checkId === 'geolocation') return;
    
    // Determine maximum variables to select
    let maxToSelect = filteredVars.length;
    
    // Respect maxVariables constraint from check config
    if (check.maxVariables) {
      maxToSelect = Math.min(maxToSelect, check.maxVariables);
    }
    
    // For survey_flow, limit to 15 variables max (ordering too many is impractical)
    if (checkId === 'survey_flow') {
      maxToSelect = Math.min(maxToSelect, 15);
    }
    
    // For single selection checks, just select the first one
    if (!check.allowMultiple) {
      maxToSelect = 1;
    }
    
    const varsToSelect = filteredVars.slice(0, maxToSelect);
    
    // Update selected variables
    setSelectedVariables(prev => ({
      ...prev,
      [checkId]: varsToSelect
    }));
    
    // Update variable order for survey flow
    if (checkId === 'survey_flow') {
      setVariableOrder(prev => ({
        ...prev,
        [checkId]: varsToSelect
      }));
    }
    
    // Clear previous results
    setCheckResults(prev => {
      const newResults = { ...prev };
      delete newResults[checkId];
      return newResults;
    });
  };

  // Clear all selected variables for a check
  const clearVariableSelection = (checkId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    setSelectedVariables(prev => ({
      ...prev,
      [checkId]: []
    }));
    
    // Clear variable order for survey flow
    if (checkId === 'survey_flow') {
      setVariableOrder(prev => ({
        ...prev,
        [checkId]: []
      }));
    }
    
    // Clear previous results
    setCheckResults(prev => {
      const newResults = { ...prev };
      delete newResults[checkId];
      return newResults;
    });
  };

  // New function to handle variable ordering for survey flow
  const moveVariable = (checkId, variableIndex, direction) => {
    const currentOrder = variableOrder[checkId] || [];
    if (currentOrder.length < 2) return;

    const newOrder = [...currentOrder];
    const targetIndex = direction === 'up' ? variableIndex - 1 : variableIndex + 1;

    if (targetIndex >= 0 && targetIndex < newOrder.length) {
      [newOrder[variableIndex], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[variableIndex]];
      
      setVariableOrder(prev => ({
        ...prev,
        [checkId]: newOrder
      }));

      // Clear results when changing order
      setCheckResults(prev => {
        const newResults = { ...prev };
        delete newResults[checkId];
        return newResults;
      });
    }
  };

  const validateChecks = () => {
    // Check if interview date is selected (mandatory)
    if (!interviewDateId) {
      setCheckError('Please select an Interview Date Column. This is required for timeline analysis.');
      return false;
    }

    for (const checkId of selectedChecks) {
      const check = DATA_QUALITY_CHECKS.find(c => c.id === checkId);
      const vars = selectedVariables[checkId] || [];
      
      if (vars.length < check.minVariables) {
        setCheckError(`Please select at least ${check.minVariables} variable(s) for ${check.name} check`);
        return false;
      }
      
      if (check.maxVariables && vars.length > check.maxVariables) {
        setCheckError(`Maximum ${check.maxVariables} variables allowed for ${check.name} check`);
        return false;
      }

      // Duration range validation for duration checks
      if (checkId === 'duration' && vars.length > 0) {
        const rangeValidation = validateDurationRange(checkId);
        if (!rangeValidation.valid) {
          setCheckError(`Duration Analysis: ${rangeValidation.message}`);
          return false;
        }
      }
    }
    return true;
  };

  const renderVariableOptions = () => {
    return (
      <div 
        className="mb-4 space-y-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-md"
        style={{display: 'flex', flexDirection: 'column', marginBottom: '15px', gap: '15px'}}
      >
        {/* Horizontal layout for the four main fields */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginTop: '15px'
        }}>
          
          {/* Respondent ID Selection */}
          <div style={{marginLeft: '5px'}}>
            <div className="flex items-center gap-2 mb-2">
              <UserSquare2 size={20} />
              <label className="font-medium" style={{marginLeft: '5px'}}>
                Respondent ID <span style={{color: '#E5814B'}}>*</span>
              </label>
            </div>
            
            <select
              value={respondentId}
              onChange={(e) => setRespondentId(e.target.value)}
              className="w-full p-2 border rounded-md bg-white dark:bg-gray-700"
              style={{
                marginTop: '5px', 
                height: '35px',
                backgroundColor: isDarkMode ? '#0d0e0eff' : '#e9e9e9ff',
                color: isDarkMode ? '#6b6b6bff' : '#6B7280', cursor: 'pointer',
                borderRadius: '40rem',
                border: isDarkMode ? '0px solid #6d6d6dff' : '0px solid #D1D5DB',
                borderColor: !respondentId ? '#E5814B' : undefined,              
              }}
              required
            >
              <option value="" style={{cursor: 'pointer'}}>Select Respondent ID (Required)</option>
              {variables.map((variable) => (
                <option key={variable} value={variable} style={{cursor: 'pointer'}}>
                 
                  {variable}
                </option>
              ))}
            </select>
            {!respondentId && (
              <div style={{
                fontSize: '12px',
                color: '#E5814B',
                marginTop: '4px',

              }}>
                Respondent ID is required for analysis
              </div>
            )}
          </div>

          {/* Data Collector ID Selection */}
          <div style={{marginLeft: '5px'}}>
            <div className="flex items-center gap-2 mb-2">
              <UserSquare2 size={20} />
              <label className="font-medium" style={{marginLeft: '5px'}}>
                Data Collector ID <span style={{color: '#E5814B'}}>*</span>
              </label>
            </div>
            <select
              value={dataCollectorId}
              onChange={(e) => setDataCollectorId(e.target.value)}
              className="w-full p-2 border rounded-md bg-white dark:bg-gray-700"
              style={{
                marginTop: '5px', 
                height: '35px',
                borderColor: !dataCollectorId ? '#E5814B' : undefined,
                marginTop: '5px', 
                height: '35px',
                borderRadius: '40rem',
                border: isDarkMode ? '0px solid #6d6d6dff' : '0px solid #D1D5DB',
                backgroundColor: isDarkMode ? '#0d0e0eff' : '#e9e9e9ff',
                color: isDarkMode ? '#6b6b6bff' : '#6B7280', cursor: 'pointer'
              }}
              required
            >
              <option value="">Select Data Collector ID (Required)</option>
              {variables.map((variable) => (
                <option key={variable} value={variable}>
                  {variable}
                </option>
              ))}
            </select>
            {!dataCollectorId && (
              <div style={{
                fontSize: '12px',
                color: '#E5814B',
                marginTop: '4px'
              }}>
                Data Collector ID is required for analysis
              </div>
            )}
          </div>

          {/* Interview Date Selection */}
          <div style={{marginLeft: '5px'}}>
            <div className="flex items-center gap-2 mb-2">
              <Calendar size={20} />
              <label className="font-medium" style={{marginLeft: '5px'}}>
                Interview Date Column <span style={{color: '#E5814B'}}>*</span>
              </label>
            </div>
            <select
              value={interviewDateId}
              onChange={(e) => setInterviewDateId(e.target.value)}
              className="w-full p-2 border rounded-md bg-white dark:bg-gray-700"
              style={{
                marginTop: '5px', 
                height: '35px',
                borderColor: !interviewDateId ? '#E5814B' : undefined,
                marginTop: '5px', 
                height: '35px',
                borderRadius: '40rem',
                border: isDarkMode ? '0px solid #6d6d6dff' : '0px solid #D1D5DB',
                backgroundColor: isDarkMode ? '#0d0e0eff' : '#e9e9e9ff',
                color: isDarkMode ? '#6b6b6bff' : '#6B7280', cursor: 'pointer'
              }}
              required
            >
              <option value="">Select Interview Date Column (Required)</option>
              {variables
                .filter(variable => {
                  // Accept every backend variant of a date/time type
                  const t = String(columnTypes[variable] || '').toLowerCase();
                  return t === 'timestamp' || t === 'datetime' || t === 'date' || t === 'datetime64';
                })
                .map((variable) => (
                  <option key={variable} value={variable}>
                    {variable} ({columnTypes[variable]})
                  </option>
                ))}
            </select>
            {!interviewDateId && (
              <div style={{
                fontSize: '12px',
                color: '#E5814B',
                marginTop: '4px'
              }}>
                Interview date column is required for timeline analysis
              </div>
            )}
          </div>

          {/* Group By Variable (optional) */}
          <div style={{marginLeft: '5px'}}>
            <div className="flex items-center gap-2 mb-2">
              <Users size={20} />
              <label className="font-medium" style={{marginLeft: '5px'}}>Group By Variable (Optional)</label>
            </div>
            <select
              value={groupByVariable}
              onChange={(e) => setGroupByVariable(e.target.value)}
              className="w-full p-2 border rounded-md bg-white dark:bg-gray-700"
              style={{marginTop: '5px', height: '35px', marginTop: '5px', 
                height: '35px',
                borderRadius: '40rem',
                border: isDarkMode ? '0px solid #6d6d6dff' : '0px solid #D1D5DB',
                backgroundColor: isDarkMode ? '#0d0e0eff' : '#e9e9e9ff',
                color: isDarkMode ? '#6b6b6bff' : '#6B7280', cursor: 'pointer' }}
            >
              <option value="">Select Group By Variable</option>
              {variables.map((variable) => (
                <option key={variable} value={variable}>
                  {variable}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  };
  
  const boundaryHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'X-Cloud-Token': localStorage.getItem('token') || '',
  });

  const submitBoundaryFile = async (file, attr) => {
    if (!projectId || !file) return;
    setBoundaryBusy(true); setBoundaryError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      if (attr) form.append('group_attr', attr);
      const resp = await fetch(`${API_BASE_URL}/api/projects/${projectId}/boundaries`, {
        method: 'POST', headers: boundaryHeaders(), body: form,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Upload failed');
      if (data.needs_attribute) {
        setBoundaryInspect(data);
        setBoundaryAttr(data.attributes?.[0] || '');
      } else {
        setBoundaryInspect(null); setBoundaryFile(null); setBoundaryAttr('');
        setBoundaryStatus({ loaded: true, group_attr: data.group_attr, groups_count: data.groups_count, source: file.name });
      }
    } catch (e) {
      setBoundaryError(e.message);
    } finally {
      setBoundaryBusy(false);
    }
  };

  const removeBoundaries = async () => {
    if (!projectId) return;
    setBoundaryBusy(true);
    try {
      await fetch(`${API_BASE_URL}/api/projects/${projectId}/boundaries`, {
        method: 'DELETE', headers: boundaryHeaders(),
      });
      setBoundaryStatus({ loaded: false });
    } catch (e) { /* non-fatal */ } finally { setBoundaryBusy(false); }
  };

  const runChecks = async () => {
    
    if (!validateChecks()) {
      return;
    }

    setIsLoadingChecks(true);
    setCheckError(null);
    
    try {
      // =====================================================================
      // PHASE 2: Server-side quality checks when projectId is available
      // Sends only project_id + config — no raw data in request body
      // Falls back to v1 (data in body) when projectId is not provided
      // =====================================================================
      const useServerSide = !!projectId;
      
      const checksToRun = selectedChecks.map(checkId => {
        const selectedVars = selectedVariables[checkId];
        
        let checkRequest;
        
        if (useServerSide) {
          // V2: Reference data by project ID — no data transfer
          checkRequest = {
            type: checkId,
            project_id: projectId,
            data_file_type: 'primary',
            variables: selectedVars,
            column_types: columnTypes || {},
            interview_date_id: interviewDateId,
            respondent_id: respondentId,
            ...(dataCollectorId && { data_collector_id: dataCollectorId }),
            ...(groupByVariable && { group_by: groupByVariable })
          };
        } else {
          // V1 fallback: Send data in body
          const filteredData = (data.full_data || data.data || []).map(row => {
            const filteredRow = {};
            selectedVars.forEach(variable => {
              if (variable in row) filteredRow[variable] = row[variable];
            });
            if (respondentId && respondentId in row) filteredRow[respondentId] = row[respondentId];
            if (dataCollectorId && dataCollectorId in row) filteredRow[dataCollectorId] = row[dataCollectorId];
            if (groupByVariable && groupByVariable in row) filteredRow[groupByVariable] = row[groupByVariable];
            if (interviewDateId && interviewDateId in row) filteredRow[interviewDateId] = row[interviewDateId];
            return filteredRow;
          });
          checkRequest = {
            type: checkId,
            data: filteredData,
            variables: selectedVars,
            column_types: columnTypes || {},
            interview_date_id: interviewDateId,
            respondent_id: respondentId,
            ...(dataCollectorId && { data_collector_id: dataCollectorId }),
            ...(groupByVariable && { group_by: groupByVariable })
          };
        }

        // Add variable order for survey flow
        if (checkId === 'survey_flow') {
          checkRequest.variable_order = variableOrder[checkId];
          if (dataCollectorId) {
            checkRequest.data_collector_id = dataCollectorId;
          }
          if (xlsformData?.parseResult) {
            checkRequest.xlsform_data = xlsformData.parseResult;
          }
          if (xlsformData?.matchingResult?.matches) {
            checkRequest.variable_mapping = xlsformData.matchingResult.matches;
          }
        }
        
        if (checkId === 'missingness' || checkId === 'outliers') {
          if (xlsformData?.parseResult) {
            checkRequest.xlsform_data = xlsformData.parseResult;
          }
          if (xlsformData?.matchingResult?.matches) {
            checkRequest.variable_mapping = xlsformData.matchingResult.matches;
          }
        }
        
        if (checkId === 'duration' && durationRanges[checkId]) {
          const range = durationRanges[checkId];
          if (range.min !== '' && range.max !== '' && 
              !isNaN(parseFloat(range.min)) && !isNaN(parseFloat(range.max))) {
            checkRequest.duration_range = {
              min: parseFloat(range.min),
              max: parseFloat(range.max)
            };
          }
        }

        if (checkId === 'text_clustering') {
          checkRequest.min_response_length = textClusteringSettings.minResponseLength;
          checkRequest.similarity_threshold = textClusteringSettings.similarityThreshold;
          if (textClusteringSettings.nClusters !== null) {
            checkRequest.n_clusters = textClusteringSettings.nClusters;
          }
          if (dataCollectorId) {
            checkRequest.data_collector_id = dataCollectorId;
          }
        }

        if (checkId === 'sentinel_phrases') {
          checkRequest.sentinel_min_words = sentinelSettings.minWords;
          checkRequest.sentinel_severity  = sentinelSettings.severity;
          if (sentinelSettings.sentinelRegex && sentinelSettings.sentinelRegex.trim()) {
            checkRequest.sentinel_regex = sentinelSettings.sentinelRegex.trim();
          }
          if (sentinelSettings.perVariableConfig && Object.keys(sentinelSettings.perVariableConfig).length) {
            checkRequest.sentinel_per_variable_config = sentinelSettings.perVariableConfig;
          }
          // Optional response-quality detectors (off by default)
          checkRequest.sentinel_detect_gibberish  = !!sentinelSettings.detectGibberish;
          checkRequest.sentinel_detect_dk_refused = !!sentinelSettings.detectDkRefused;
          checkRequest.sentinel_detect_encoding   = !!sentinelSettings.detectEncoding;
          if (dataCollectorId) {
            checkRequest.data_collector_id = dataCollectorId;
          }
        }

        if (checkId === 'gated_fields') {
          // Backend expects snake_case keys inside each rule, and the field set
          // varies by mode (gate vs date_order vs age_birth_year vs numeric_range).
          checkRequest.gate_rules = (gatedFieldsSettings.rules || []).map(r => {
            const base = {
              rule_id:  r.ruleId,
              label:    r.label,
              mode:     r.mode || 'expect_content',
              severity: r.severity,
            };
            const mode = r.mode || 'expect_content';

            if (mode === 'expect_content' || mode === 'expect_empty') {
              return {
                ...base,
                follow_up_var: r.followUpVar,
                gate_var:      r.gateVar,
                gate_values:   r.gateValues,
                min_words:     r.minWords,
              };
            }
            if (mode === 'date_order') {
              return {
                ...base,
                start_var:   r.startVar,
                end_var:     r.endVar,
                allow_equal: r.allowEqual !== false,
              };
            }
            if (mode === 'age_birth_year') {
              return {
                ...base,
                age_var:            r.ageVar,
                birth_year_var:     r.birthYearVar,
                interview_date_var: r.interviewDateVar || null,
                tolerance:          parseInt(r.tolerance ?? 1, 10) || 1,
              };
            }
            if (mode === 'numeric_range') {
              const parseNum = (v) => {
                if (v === '' || v === null || v === undefined) return null;
                const n = Number(v);
                return Number.isFinite(n) ? n : null;
              };
              return {
                ...base,
                target_var: r.targetVar,
                min_value:  parseNum(r.minValue),
                max_value:  parseNum(r.maxValue),
              };
            }
            return base;
          });
          checkRequest.gate_default_min_words = gatedFieldsSettings.defaultMinWords ?? 15;
          checkRequest.gate_default_severity  = gatedFieldsSettings.defaultSeverity ?? 'high';
          // gated_fields doesn't use `variables` for selection — rules are authoritative —
          // but the backend accepts an empty list:
          checkRequest.variables = [];
          if (dataCollectorId) {
            checkRequest.data_collector_id = dataCollectorId;
          }
        }

        if (checkId === 'probe_depth') {
          checkRequest.probe_min_words       = probeDepthSettings?.minWords ?? 15;
          checkRequest.probe_score_threshold = probeDepthSettings?.scoreThreshold ?? 35;
          checkRequest.probe_severity        = probeDepthSettings?.severity ?? 'medium';
          if (probeDepthSettings?.weights) {
            checkRequest.probe_weights = probeDepthSettings.weights;
          }
          if (probeDepthSettings?.domainTokensExtra && probeDepthSettings.domainTokensExtra.length) {
            // Merge extras into per-variable config so backend defaults stay intact
            checkRequest.probe_per_variable_config = Object.fromEntries(
              (selectedVars || []).map(v => [v, {
                ...(probeDepthSettings.perVariableConfig?.[v] || {}),
                domain_tokens_extra: probeDepthSettings.domainTokensExtra,
              }])
            );
          } else if (probeDepthSettings?.perVariableConfig
                     && Object.keys(probeDepthSettings.perVariableConfig).length) {
            checkRequest.probe_per_variable_config = probeDepthSettings.perVariableConfig;
          }
          if (dataCollectorId) {
            checkRequest.data_collector_id = dataCollectorId;
          }
        }

        if (checkId === 'straight_lining') {
          // Auto-detect when no variables selected (backend treats empty list as auto-detect)
          checkRequest.variables = selectedVars || [];
          checkRequest.sl_variance_threshold   = straightLiningSettings?.varianceThreshold   ?? 0.5;
          checkRequest.sl_entropy_threshold    = straightLiningSettings?.entropyThreshold    ?? 1.0;
          checkRequest.sl_repetition_threshold = straightLiningSettings?.repetitionThreshold ?? 0.8;
          checkRequest.sl_flag_score_cutoff    = straightLiningSettings?.flagScoreCutoff     ?? 0.5;
          checkRequest.sl_severity             = straightLiningSettings?.severity            ?? 'medium';
          if (straightLiningSettings?.methodWeights
              && Object.keys(straightLiningSettings.methodWeights).length) {
            checkRequest.sl_method_weights = straightLiningSettings.methodWeights;
          }
          if (dataCollectorId) {
            checkRequest.data_collector_id = dataCollectorId;
          }
        }

        return checkRequest;
      });

      console.log(`Running quality checks via ${useServerSide ? 'V2 (server-side)' : 'V1 (data in body)'}:`, checksToRun);

      const endpoint = useServerSide 
        ? `${API_BASE_URL}/run-quality-checks-v2`
        : `${API_BASE_URL}/run-quality-checks`;
      
      const headers = { 'Content-Type': 'application/json' };
      if (useServerSide) {
        const token = localStorage.getItem('token');
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (userInfo?.email) headers['user_email'] = userInfo.email;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(checksToRun)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || JSON.stringify(errorData, null, 2);
        } catch (e) {
          errorMessage = errorText;
        }
        
        throw new Error(`Request failed: ${errorMessage}`);
      }
      
      const results = await response.json();
      console.log('DEBUG: Frontend - Success response:', results);

      setCheckResults(results.reduce((acc, result) => {
        if (result && result.check_type && result.results) {
          acc[result.check_type] = result.results;
        }
        return acc;
      }, {}));
      
      // Navigate to dashboard immediately — land on Issue Heatmap
      if (onNavigate) {
        if (setActiveQualityTab) setActiveQualityTab(null);
        onNavigate('dashboard');
      }
      
    } catch (error) {
      console.error('Error running checks:', error);
      setCheckError(error.message || 'An error occurred while running quality checks');
      
    } finally {
      setIsLoadingChecks(false);
    }
  };

  // Render error message
  const renderError = () => {
    if (!checkError) return null;
    
    return (
      <div 
        style={{
          backgroundColor: isDarkMode ? '#442C2C' : '#FEE2E2',
          border: `1px solid ${isDarkMode ? '#E5814B' : '#E5814B'}`,
          color: isDarkMode ? '#FCA5A5' : '#E5814B',
          padding: '1rem',
          borderRadius: '0.375rem',
          marginBottom: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <AlertCircle size={20} />
          <span style={{ fontWeight: 'bold' }}>Error Details:</span>
        </div>
        <pre style={{ 
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: 'monospace',
          fontSize: '0.8em',
          margin: 0,
          padding: '0.5rem',
          backgroundColor: isDarkMode ? '#3C2424' : '#FECACA',
          borderRadius: '0.25rem'
        }}>
          {typeof checkError === 'object' ? JSON.stringify(checkError, null, 2) : checkError}
        </pre>
      </div>
    );
  };

  // Survey Flow Variable Ordering Component
  const renderSurveyFlowOrdering = (checkId, selectedVars) => {
    const currentOrder = variableOrder[checkId] || selectedVars;
    
    return (
      <div style={{
        marginTop: '1rem',
        padding: '0.75rem',
        backgroundColor: isDarkMode ? '#1c1d1fff' : '#F3F4F6',
        borderRadius: '0.5rem',
        border: isDarkMode ? '1px solid #1c1d1dff' : '1px solid #D1D5DB' 
      }}>
        <h5 style={{ 
          fontSize: '0.8rem', 
          fontWeight: '600', 
          marginBottom: '0.5rem',
          color: isDarkMode ? '#aaaaaaff' : '#374151'
        }}>
          Survey Flow Order (drag or use arrows to reorder):
        </h5>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {currentOrder.map((variable, index) => (
            <div key={variable} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.5rem',
              border: isDarkMode ? '1px solid #313233' : '1px solid #D1D5DB',
              backgroundColor: isDarkMode ? '#313233ff' : '#FFFFFF',
              color: isDarkMode ? '#aaaaaaff' : '#353638ff',
            }}>
              <span style={{ 
                fontSize: '0.8rem',
                color: isDarkMode ? '#aaaaaaff' : '#374151'
              }}>
                {index + 1}. {variable}
              </span>
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent card deselection
                    moveVariable(checkId, index, 'up');
                  }}
                  disabled={index === 0}
                  style={{
                    padding: '0.25rem',
                    backgroundColor: index === 0 ? (isDarkMode ? '#4B5563' : '#F3F4F6') : (isDarkMode ? '#6B7280' : '#E5E7EB'),
                    border: 'none',
                    borderRadius: '0.25rem',
                    cursor: index === 0 ? 'not-allowed' : 'pointer',
                    color: isDarkMode ? '#aaaaaaff' : '#374151'
                  }}
                >
                  <ArrowUp size={16} />
                </button>
                <button
                  onClick={() => moveVariable(checkId, index, 'down')}
                  disabled={index === currentOrder.length - 1}
                  style={{
                    padding: '0.25rem',
                    backgroundColor: index === currentOrder.length - 1 ? (isDarkMode ? '#4B5563' : '#F3F4F6') : (isDarkMode ? '#6B7280' : '#E5E7EB'),
                    border: 'none',
                    borderRadius: '0.25rem',
                    cursor: index === currentOrder.length - 1 ? 'not-allowed' : 'pointer',
                    color: isDarkMode ? '#aaaaaaff' : '#374151'
                  }}
                >
                  <ArrowDown size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Duration Range Input Component
  const renderDurationRangeInput = (checkId, selectedVars) => {
    if (selectedVars.length === 0) return null;

    const range = durationRanges[checkId] || { min: '', max: '' };
    const validation = validateDurationRange(checkId);

    return (
      <div 
        style={{
          marginTop: '1rem',
          padding: '0.75rem',
            backgroundColor: isDarkMode ? '#1c1d1fff' : '#F3F4F6',
            borderRadius: '0.5rem',
            border: isDarkMode ? '1px solid #1c1d1dff' : '1px solid #D1D5DB' 
        }}
        onClick={(e) => e.stopPropagation()} // Prevent card deselection
      >
        <h5 style={{ 
          fontSize: '0.8rem', 
          fontWeight: '600', 
          marginBottom: '0.5rem',
          color: isDarkMode ? '#aaaaaaff' : '#374151'
        }}>
          Acceptable Duration Range (Required):
        </h5>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '0.5rem' }}>
          <div style={{ flex: 1 }}>
            <label style={{ 
              fontSize: '0.75rem', 
              color: isDarkMode ? '#9CA3AF' : '#6B7280',
              display: 'block',
              marginBottom: '0.25rem'
            }}>
              Minimum Duration:
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={range.min}
              onChange={(e) => {
                e.stopPropagation(); // Prevent event bubbling
                handleDurationRangeChange(checkId, 'min', e.target.value);
              }}
              onClick={(e) => e.stopPropagation()} // Prevent card deselection on click
              onFocus={(e) => e.stopPropagation()} // Prevent card deselection on focus
              
              placeholder="e.g., 5"
              style={{
                width: '90%',
                padding: '0.5rem',
                borderRadius: '0.25rem',
                border: isDarkMode ? '1px solid #313233' : '1px solid #D1D5DB',
                backgroundColor: isDarkMode ? '#313233ff' : '#FFFFFF',
                color: isDarkMode ? '#aaaaaaff' : '#353638ff',
                fontSize: '0.8rem'
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ 
              fontSize: '0.75rem', 
              color: isDarkMode ? '#9CA3AF' : '#6B7280',
              display: 'block',
              marginBottom: '0.25rem'
            }}>
              Maximum Duration:
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={range.max}
              onChange={(e) => {
                e.stopPropagation(); // Prevent event bubbling
                handleDurationRangeChange(checkId, 'max', e.target.value);
              }}
              onClick={(e) => e.stopPropagation()} // Prevent card deselection on click
              onFocus={(e) => e.stopPropagation()} // Prevent card deselection on focus
              placeholder="e.g., 60"
              style={{
                width: '90%',
                padding: '0.5rem',
                borderRadius: '0.25rem',
                border: isDarkMode ? '1px solid #313233' : '1px solid #D1D5DB',
                backgroundColor: isDarkMode ? '#313233ff' : '#FFFFFF',
                color: isDarkMode ? '#aaaaaaff' : '#353638ff',
                fontSize: '0.8rem'
              }}
            />
          </div>
        </div>
        
        {!validation.valid && (range.min !== '' || range.max !== '') && (
          <div style={{
            fontSize: '0.75rem',
            color: '#ef5844ff',
            marginTop: '0.25rem'
          }}>
            {validation.message}
          </div>
        )}
        
        <div style={{
          fontSize: '0.75rem',
          color: isDarkMode ? '#9CA3AF' : '#6B7280',
          marginTop: '0.5rem'
        }}>
          Values outside this range will be flagged as outliers in red on the heatmap visualization.
        </div>
      </div>
    );
  };

  // Render text clustering settings
  const renderTextClusteringSettings = (checkId, selectedVars) => {
    if (selectedVars.length === 0) return null;

    return (
      <div 
        style={{
          marginTop: '1rem',
          padding: '0.75rem',
          backgroundColor: isDarkMode ? '#1c1d1fff' : '#F3F4F6',
          borderRadius: '0.5rem',
          border: isDarkMode ? '1px solid #1c1d1dff' : '1px solid #D1D5DB' 
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h5 style={{ 
          fontSize: '0.8rem', 
          fontWeight: '600', 
          marginBottom: '0.5rem',
          color: isDarkMode ? '#aaaaaaff' : '#374151'
        }}>
          Text Analysis Settings:
        </h5>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 120px' }}>
            <label style={{ 
              fontSize: '0.75rem', 
              color: isDarkMode ? '#9CA3AF' : '#6B7280',
              display: 'block',
              marginBottom: '0.25rem'
            }}>
              Min Words:
            </label>
            <input
              type="number"
              min="1"
              max="20"
              value={textClusteringSettings.minResponseLength}
              onChange={(e) => {
                e.stopPropagation();
                setTextClusteringSettings(prev => ({
                  ...prev,
                  minResponseLength: parseInt(e.target.value) || 3
                }));
              }}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              placeholder="3"
              style={{
                width: '90%',
                padding: '0.5rem',
                borderRadius: '0.25rem',
                border: isDarkMode ? '1px solid #313233' : '1px solid #D1D5DB',
                backgroundColor: isDarkMode ? '#313233ff' : '#FFFFFF',
                color: isDarkMode ? '#aaaaaaff' : '#353638ff',
                fontSize: '0.8rem'
              }}
            />
          </div>
          <div style={{ flex: '1 1 120px' }}>
            <label style={{ 
              fontSize: '0.75rem', 
              color: isDarkMode ? '#9CA3AF' : '#6B7280',
              display: 'block',
              marginBottom: '0.25rem'
            }}>
              Clusters (blank=auto):
            </label>
            <input
              type="number"
              min="2"
              max="20"
              value={textClusteringSettings.nClusters || ''}
              onChange={(e) => {
                e.stopPropagation();
                const val = e.target.value ? parseInt(e.target.value) : null;
                setTextClusteringSettings(prev => ({
                  ...prev,
                  nClusters: val
                }));
              }}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              placeholder="Auto"
              style={{
                width: '90%',
                padding: '0.5rem',
                borderRadius: '0.25rem',
                border: isDarkMode ? '1px solid #313233' : '1px solid #D1D5DB',
                backgroundColor: isDarkMode ? '#313233ff' : '#FFFFFF',
                color: isDarkMode ? '#aaaaaaff' : '#353638ff',
                fontSize: '0.8rem'
              }}
            />
          </div>
          <div style={{ flex: '1 1 140px' }}>
            <label style={{ 
              fontSize: '0.75rem', 
              color: isDarkMode ? '#9CA3AF' : '#6B7280',
              display: 'block',
              marginBottom: '0.25rem'
            }}>
              Similarity Threshold:
            </label>
            <input
              type="number"
              min="0.5"
              max="0.99"
              step="0.05"
              value={textClusteringSettings.similarityThreshold}
              onChange={(e) => {
                e.stopPropagation();
                setTextClusteringSettings(prev => ({
                  ...prev,
                  similarityThreshold: parseFloat(e.target.value) || 0.85
                }));
              }}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              placeholder="0.85"
              style={{
                width: '90%',
                padding: '0.5rem',
                borderRadius: '0.25rem',
                border: isDarkMode ? '1px solid #313233' : '1px solid #D1D5DB',
                backgroundColor: isDarkMode ? '#313233ff' : '#FFFFFF',
                color: isDarkMode ? '#aaaaaaff' : '#353638ff',
                fontSize: '0.8rem'
              }}
            />
          </div>
        </div>
        
        <div style={{
          fontSize: '0.75rem',
          color: isDarkMode ? '#9CA3AF' : '#6B7280',
          marginTop: '0.5rem'
        }}>
          Responses with fewer words are filtered. Higher similarity threshold (0.85-0.95) catches more suspicious pairs.
        </div>
      </div>
    );
  };

  // ----------------------------------------------------------------------
  // Small style helpers for the sentinel + gated settings UIs
  // ----------------------------------------------------------------------
  const _settingsInputStyle = (isDark) => ({
    width: '100%',
    padding: '0.4rem 0.5rem',
    borderRadius: '0.25rem',
    border: isDark ? '1px solid #313233' : '1px solid #D1D5DB',
    backgroundColor: isDark ? '#313233ff' : '#FFFFFF',
    color: isDark ? '#aaaaaaff' : '#353638ff',
    fontSize: '0.8rem',
  });
  const _settingsLabelStyle = (isDark) => ({
    fontSize: '0.7rem',
    color: isDark ? '#9CA3AF' : '#6B7280',
    display: 'block',
    marginBottom: '0.2rem',
  });

  // ----------------------------------------------------------------------
  // Render Sentinel Phrase settings
  // ----------------------------------------------------------------------
  const renderSentinelPhraseSettings = (checkId, selectedVars) => {
    if (selectedVars.length === 0) return null;
    return (
      <div
        style={{
          marginTop: '1rem',
          padding: '0.75rem',
          backgroundColor: isDarkMode ? '#1c1d1fff' : '#F3F4F6',
          borderRadius: '0.5rem',
          border: isDarkMode ? '1px solid #1c1d1dff' : '1px solid #D1D5DB',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h5 style={{
          fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.5rem',
          color: isDarkMode ? '#aaaaaaff' : '#374151',
        }}>
          Sentinel Phrase Settings:
        </h5>

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
          <div style={{ flex: '1 1 120px' }}>
            <label style={_settingsLabelStyle(isDarkMode)}>Min Words:</label>
            <input
              type="number" min="1" max="100"
              value={sentinelSettings.minWords}
              onChange={(e) => {
                e.stopPropagation();
                setSentinelSettings(prev => ({ ...prev, minWords: parseInt(e.target.value) || 15 }));
              }}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              placeholder="15"
              style={_settingsInputStyle(isDarkMode)}
            />
          </div>

          <div style={{ flex: '1 1 140px' }}>
            <label style={_settingsLabelStyle(isDarkMode)}>Severity:</label>
            <select
              value={sentinelSettings.severity}
              onChange={(e) => {
                e.stopPropagation();
                setSentinelSettings(prev => ({ ...prev, severity: e.target.value }));
              }}
              onClick={(e) => e.stopPropagation()}
              style={_settingsInputStyle(isDarkMode)}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div style={{ flex: '2 1 280px' }}>
            <label style={_settingsLabelStyle(isDarkMode)}>
              Sentinel Regex (blank = backend default):
            </label>
            <input
              type="text"
              value={sentinelSettings.sentinelRegex}
              onChange={(e) => {
                e.stopPropagation();
                setSentinelSettings(prev => ({ ...prev, sentinelRegex: e.target.value }));
              }}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              placeholder="\b(none|nothing|no others?|that's it|...)\b"
              style={{ ..._settingsInputStyle(isDarkMode), fontFamily: 'ui-monospace, monospace' }}
            />
          </div>
        </div>

        {/* Response-quality detectors (opt-in) */}
        <div style={{
          marginTop: '0.75rem',
          paddingTop: '0.75rem',
          borderTop: isDarkMode ? '1px dashed #2a2b2dff' : '1px dashed #D1D5DB',
        }}>
          <div style={{
            fontSize: '0.72rem', fontWeight: '600',
            color: isDarkMode ? '#9CA3AF' : '#4B5563',
            textTransform: 'uppercase', letterSpacing: '0.04em',
            marginBottom: '0.5rem',
          }}>
            Additional response-quality flags (optional)
          </div>

          <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
            <label
              style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                fontSize: '0.78rem', cursor: 'pointer',
                color: isDarkMode ? '#D1D5DB' : '#374151',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={!!sentinelSettings.detectGibberish}
                onChange={(e) => {
                  e.stopPropagation();
                  setSentinelSettings(prev => ({ ...prev, detectGibberish: e.target.checked }));
                }}
                onClick={(e) => e.stopPropagation()}
                style={{ cursor: 'pointer' }}
              />
              Detect gibberish
            </label>

            <label
              style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                fontSize: '0.78rem', cursor: 'pointer',
                color: isDarkMode ? '#D1D5DB' : '#374151',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={!!sentinelSettings.detectDkRefused}
                onChange={(e) => {
                  e.stopPropagation();
                  setSentinelSettings(prev => ({ ...prev, detectDkRefused: e.target.checked }));
                }}
                onClick={(e) => e.stopPropagation()}
                style={{ cursor: 'pointer' }}
              />
              Detect "don't know" / refused
            </label>

            <label
              style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                fontSize: '0.78rem', cursor: 'pointer',
                color: isDarkMode ? '#D1D5DB' : '#374151',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={!!sentinelSettings.detectEncoding}
                onChange={(e) => {
                  e.stopPropagation();
                  setSentinelSettings(prev => ({ ...prev, detectEncoding: e.target.checked }));
                }}
                onClick={(e) => e.stopPropagation()}
                style={{ cursor: 'pointer' }}
              />
              Detect encoding artifacts
            </label>
          </div>

          <div style={{
            fontSize: '0.7rem',
            color: isDarkMode ? '#6B7280' : '#9CA3AF',
            marginTop: '0.4rem',
            lineHeight: 1.4,
          }}>
            Gibberish flags consonant strings & repeated chars; DK/refused flags filler responses;
            encoding flags mojibake (Ã, â€) signaling UTF-8 corruption upstream.
          </div>
        </div>

        <div style={{
          fontSize: '0.72rem',
          color: isDarkMode ? '#9CA3AF' : '#6B7280',
          marginTop: '0.5rem',
        }}>
          A response is flagged if word count is below the threshold AND the regex doesn't match.
          Useful for catching skipped closing probes ("anything else?" → "Amen.").
        </div>
      </div>
    );
  };

  // ----------------------------------------------------------------------
  // Render Gated-Field rules editor
  // ----------------------------------------------------------------------
  const renderGatedFieldsSettings = (checkId) => {
    const rules = gatedFieldsSettings.rules || [];

    const updateRule = (idx, patch) => {
      setGatedFieldsSettings(prev => ({
        ...prev,
        rules: prev.rules.map((r, i) => i === idx ? { ...r, ...patch } : r),
      }));
    };
    const addRule = () => {
      setGatedFieldsSettings(prev => ({
        ...prev,
        rules: [...prev.rules, {
          ruleId:        `rule_${Date.now()}`,
          label:         '',
          mode:          'expect_content',
          severity:      prev.defaultSeverity || 'high',
          // Gate-mode fields
          followUpVar:   '',
          gateVar:       '',
          gateValues:    [],
          minWords:      prev.defaultMinWords || 15,
          // Date-order fields
          startVar:      '',
          endVar:        '',
          allowEqual:    true,
          // Age/birth-year fields
          ageVar:           '',
          birthYearVar:     '',
          interviewDateVar: '',
          tolerance:        1,
          // Numeric-range fields
          targetVar:     '',
          minValue:      '',
          maxValue:      '',
        }],
      }));
    };
    const removeRule = (idx) => {
      setGatedFieldsSettings(prev => ({
        ...prev,
        rules: prev.rules.filter((_, i) => i !== idx),
      }));
    };

    return (
      <div
        style={{
          marginTop: '1rem',
          padding: '0.75rem',
          backgroundColor: isDarkMode ? '#1c1d1fff' : '#F3F4F6',
          borderRadius: '0.5rem',
          border: isDarkMode ? '1px solid #1c1d1dff' : '1px solid #D1D5DB',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.75rem',
        }}>
          <h5 style={{
            fontSize: '0.8rem',
            fontWeight: '600',
            margin: 0,
            color: isDarkMode ? '#aaaaaaff' : '#374151',
          }}>
            Gate Rules ({rules.length})
          </h5>
          <button
            onClick={(e) => { e.stopPropagation(); addRule(); }}
            style={{
              padding: '4px 10px',
              fontSize: '0.75rem',
              borderRadius: '6px',
              border: '1px solid #3B82F6',
              background: 'transparent',
              color: '#3B82F6',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <Plus size={12} /> Add rule
          </button>
        </div>

        {rules.length === 0 && (
          <div style={{
            fontSize: '0.75rem',
            color: isDarkMode ? '#9CA3AF' : '#6B7280',
            padding: '0.5rem 0',
          }}>
            No rules yet. Click <strong>Add rule</strong> to define your first gate-follow-up pair.
          </div>
        )}

        {rules.map((r, idx) => (
          <div
            key={r.ruleId}
            style={{
              marginBottom: '0.5rem',
              padding: '0.5rem',
              borderRadius: '0.375rem',
              border: isDarkMode ? '1px solid #2D2E32' : '1px solid #E5E7EB',
              backgroundColor: isDarkMode ? '#17171b' : '#FFFFFF',
            }}
          >
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: '0.5rem',
              marginBottom: '0.5rem',
            }}>
              <div>
                <label style={_settingsLabelStyle(isDarkMode)}>Label:</label>
                <input
                  type="text"
                  value={r.label}
                  placeholder="e.g. Training disclosure"
                  onChange={(e) => updateRule(idx, { label: e.target.value })}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  style={_settingsInputStyle(isDarkMode)}
                />
              </div>

              <div>
                <label style={_settingsLabelStyle(isDarkMode)}>Mode:</label>
                <select
                  value={r.mode}
                  onChange={(e) => updateRule(idx, { mode: e.target.value })}
                  onClick={(e) => e.stopPropagation()}
                  style={_settingsInputStyle(isDarkMode)}
                >
                  <optgroup label="Gate consistency">
                    <option value="expect_content">expect_content (gate=yes → must answer)</option>
                    <option value="expect_empty">expect_empty (gate=no → must be empty)</option>
                  </optgroup>
                  <optgroup label="Logical consistency">
                    <option value="date_order">date_order (start ≤ end)</option>
                    <option value="age_birth_year">age_birth_year (age matches birth year)</option>
                    <option value="numeric_range">numeric_range (value in [min, max])</option>
                  </optgroup>
                </select>
              </div>

              {/* Gate-mode fields */}
              {(r.mode === 'expect_content' || r.mode === 'expect_empty') && (
                <>
                  <div>
                    <label style={_settingsLabelStyle(isDarkMode)}>Follow-up var:</label>
                    <select
                      value={r.followUpVar}
                      onChange={(e) => updateRule(idx, { followUpVar: e.target.value })}
                      onClick={(e) => e.stopPropagation()}
                      style={_settingsInputStyle(isDarkMode)}
                    >
                      <option value="">(pick variable)</option>
                      {variables.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>

                  <div>
                    <label style={_settingsLabelStyle(isDarkMode)}>Gate var:</label>
                    <select
                      value={r.gateVar}
                      onChange={(e) => updateRule(idx, { gateVar: e.target.value })}
                      onClick={(e) => e.stopPropagation()}
                      style={_settingsInputStyle(isDarkMode)}
                    >
                      <option value="">(pick variable)</option>
                      {variables.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>

                  <div>
                    <label style={_settingsLabelStyle(isDarkMode)}>Gate values (comma-sep):</label>
                    <input
                      type="text"
                      value={(r.gateValues || []).join(', ')}
                      placeholder="1, yes, Yes"
                      onChange={(e) => updateRule(idx, {
                        gateValues: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
                      })}
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                      style={_settingsInputStyle(isDarkMode)}
                    />
                  </div>

                  <div>
                    <label style={_settingsLabelStyle(isDarkMode)}>Min words:</label>
                    <input
                      type="number" min="0" max="200"
                      value={r.minWords}
                      onChange={(e) => updateRule(idx, { minWords: parseInt(e.target.value) || 0 })}
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                      style={_settingsInputStyle(isDarkMode)}
                    />
                  </div>
                </>
              )}

              {/* Date-order fields */}
              {r.mode === 'date_order' && (
                <>
                  <div>
                    <label style={_settingsLabelStyle(isDarkMode)}>Start var:</label>
                    <select
                      value={r.startVar || ''}
                      onChange={(e) => updateRule(idx, { startVar: e.target.value })}
                      onClick={(e) => e.stopPropagation()}
                      style={_settingsInputStyle(isDarkMode)}
                    >
                      <option value="">(pick variable)</option>
                      {variables.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>

                  <div>
                    <label style={_settingsLabelStyle(isDarkMode)}>End var:</label>
                    <select
                      value={r.endVar || ''}
                      onChange={(e) => updateRule(idx, { endVar: e.target.value })}
                      onClick={(e) => e.stopPropagation()}
                      style={_settingsInputStyle(isDarkMode)}
                    >
                      <option value="">(pick variable)</option>
                      {variables.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <label
                      style={{ display: 'flex', alignItems: 'center', gap: '0.4rem',
                               fontSize: '0.78rem', cursor: 'pointer',
                               color: isDarkMode ? '#D1D5DB' : '#374151' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={r.allowEqual !== false}
                        onChange={(e) => updateRule(idx, { allowEqual: e.target.checked })}
                        onClick={(e) => e.stopPropagation()}
                      />
                      Allow start = end
                    </label>
                  </div>
                </>
              )}

              {/* Age / birth-year fields */}
              {r.mode === 'age_birth_year' && (
                <>
                  <div>
                    <label style={_settingsLabelStyle(isDarkMode)}>Age var:</label>
                    <select
                      value={r.ageVar || ''}
                      onChange={(e) => updateRule(idx, { ageVar: e.target.value })}
                      onClick={(e) => e.stopPropagation()}
                      style={_settingsInputStyle(isDarkMode)}
                    >
                      <option value="">(pick variable)</option>
                      {variables.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>

                  <div>
                    <label style={_settingsLabelStyle(isDarkMode)}>Birth-year var:</label>
                    <select
                      value={r.birthYearVar || ''}
                      onChange={(e) => updateRule(idx, { birthYearVar: e.target.value })}
                      onClick={(e) => e.stopPropagation()}
                      style={_settingsInputStyle(isDarkMode)}
                    >
                      <option value="">(pick variable)</option>
                      {variables.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>

                  <div>
                    <label style={_settingsLabelStyle(isDarkMode)}>Interview-date var (optional):</label>
                    <select
                      value={r.interviewDateVar || ''}
                      onChange={(e) => updateRule(idx, { interviewDateVar: e.target.value })}
                      onClick={(e) => e.stopPropagation()}
                      style={_settingsInputStyle(isDarkMode)}
                    >
                      <option value="">(use today's year)</option>
                      {variables.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>

                  <div>
                    <label style={_settingsLabelStyle(isDarkMode)}>Tolerance (years):</label>
                    <input
                      type="number" min="0" max="10"
                      value={r.tolerance ?? 1}
                      onChange={(e) => updateRule(idx, { tolerance: parseInt(e.target.value) || 0 })}
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                      style={_settingsInputStyle(isDarkMode)}
                    />
                  </div>
                </>
              )}

              {/* Numeric-range fields */}
              {r.mode === 'numeric_range' && (
                <>
                  <div>
                    <label style={_settingsLabelStyle(isDarkMode)}>Target var:</label>
                    <select
                      value={r.targetVar || ''}
                      onChange={(e) => updateRule(idx, { targetVar: e.target.value })}
                      onClick={(e) => e.stopPropagation()}
                      style={_settingsInputStyle(isDarkMode)}
                    >
                      <option value="">(pick variable)</option>
                      {variables.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>

                  <div>
                    <label style={_settingsLabelStyle(isDarkMode)}>Min value (blank = none):</label>
                    <input
                      type="number" step="any"
                      value={r.minValue ?? ''}
                      placeholder="0"
                      onChange={(e) => updateRule(idx, { minValue: e.target.value })}
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                      style={_settingsInputStyle(isDarkMode)}
                    />
                  </div>

                  <div>
                    <label style={_settingsLabelStyle(isDarkMode)}>Max value (blank = none):</label>
                    <input
                      type="number" step="any"
                      value={r.maxValue ?? ''}
                      placeholder="100"
                      onChange={(e) => updateRule(idx, { maxValue: e.target.value })}
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                      style={_settingsInputStyle(isDarkMode)}
                    />
                  </div>
                </>
              )}

              <div>
                <label style={_settingsLabelStyle(isDarkMode)}>Severity:</label>
                <select
                  value={r.severity}
                  onChange={(e) => updateRule(idx, { severity: e.target.value })}
                  onClick={(e) => e.stopPropagation()}
                  style={_settingsInputStyle(isDarkMode)}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                <button
                  onClick={(e) => { e.stopPropagation(); removeRule(idx); }}
                  style={{
                    padding: '6px 10px',
                    fontSize: '0.75rem',
                    borderRadius: '6px',
                    border: '1px solid #EF4444',
                    background: 'transparent',
                    color: '#EF4444',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Trash2 size={12} /> Remove
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderStraightLiningSettings = (checkId) => {
    const _input = {
      width: '90%',
      padding: '0.5rem',
      borderRadius: '0.25rem',
      border: isDarkMode ? '1px solid #313233' : '1px solid #D1D5DB',
      backgroundColor: isDarkMode ? '#313233ff' : '#FFFFFF',
      color: isDarkMode ? '#aaaaaaff' : '#353638ff',
      fontSize: '0.875rem',
    };
    const _label = {
      fontSize: '0.75rem',
      color: isDarkMode ? '#9CA3AF' : '#6B7280',
      display: 'block',
      marginBottom: '0.25rem',
    };

    const settings = straightLiningSettings || {};
    const updateSetting = (key, value) => {
      setStraightLiningSettings(prev => ({ ...(prev || {}), [key]: value }));
    };

    return (
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
        marginTop: '1rem', padding: '1rem',
        backgroundColor: isDarkMode ? '#1c1d1fff' : '#F3F4F6',
        borderRadius: '0.5rem',
      }}>
        <div style={{
          fontSize: '0.875rem', fontWeight: 500,
          color: isDarkMode ? '#aaaaaaff' : '#1F2937',
          marginBottom: '0.5rem',
        }}>
          Straight-Lining Settings:
        </div>
        <div style={{
          fontSize: '0.75rem',
          color: isDarkMode ? '#9CA3AF' : '#6B7280',
          marginBottom: '0.875rem',
          lineHeight: 1.5,
        }}>
          Leave variables unselected above to auto-detect numeric scale columns. Five
          methods (variance, entropy, repetition, IQR, Isolation Forest) score each
          respondent; the combined score determines severity.
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '0.75rem',
          marginBottom: '0.75rem',
        }}>
          <div>
            <label style={_label}>Variance threshold</label>
            <input
              type="number" step="0.05" min="0" max="5"
              style={_input}
              value={settings.varianceThreshold ?? 0.5}
              onChange={(e) => updateSetting('varianceThreshold', parseFloat(e.target.value) || 0)}
            />
            <div style={{ fontSize: '0.7rem', color: isDarkMode ? '#6B7280' : '#9CA3AF', marginTop: '0.2rem' }}>
              Per-respondent variance below this → flag (default 0.5)
            </div>
          </div>

          <div>
            <label style={_label}>Entropy threshold</label>
            <input
              type="number" step="0.1" min="0" max="5"
              style={_input}
              value={settings.entropyThreshold ?? 1.0}
              onChange={(e) => updateSetting('entropyThreshold', parseFloat(e.target.value) || 0)}
            />
            <div style={{ fontSize: '0.7rem', color: isDarkMode ? '#6B7280' : '#9CA3AF', marginTop: '0.2rem' }}>
              Shannon entropy below this → flag (default 1.0)
            </div>
          </div>

          <div>
            <label style={_label}>Repetition threshold</label>
            <input
              type="number" step="0.05" min="0" max="1"
              style={_input}
              value={settings.repetitionThreshold ?? 0.8}
              onChange={(e) => updateSetting('repetitionThreshold', parseFloat(e.target.value) || 0)}
            />
            <div style={{ fontSize: '0.7rem', color: isDarkMode ? '#6B7280' : '#9CA3AF', marginTop: '0.2rem' }}>
              Fraction of repeated values above this → flag (default 0.8)
            </div>
          </div>

          <div>
            <label style={_label}>Flag score cutoff</label>
            <input
              type="number" step="0.05" min="0" max="1"
              style={_input}
              value={settings.flagScoreCutoff ?? 0.5}
              onChange={(e) => updateSetting('flagScoreCutoff', parseFloat(e.target.value) || 0)}
            />
            <div style={{ fontSize: '0.7rem', color: isDarkMode ? '#6B7280' : '#9CA3AF', marginTop: '0.2rem' }}>
              Combined severity score above this → flagged (default 0.5)
            </div>
          </div>

          <div>
            <label style={_label}>Default severity</label>
            <select
              style={_input}
              value={settings.severity ?? 'medium'}
              onChange={(e) => updateSetting('severity', e.target.value)}
            >
              <option value="low">low</option>
              <option value="medium">medium</option>
              <option value="high">high</option>
            </select>
          </div>
        </div>
      </div>
    );
  };

  const renderProbeDepthSettings = (checkId, selectedVars) => {
    const _input = {
      width: '90%',
      padding: '0.5rem',
      borderRadius: '0.25rem',
      border: isDarkMode ? '1px solid #313233' : '1px solid #D1D5DB',
      backgroundColor: isDarkMode ? '#313233ff' : '#FFFFFF',
      color: isDarkMode ? '#aaaaaaff' : '#353638ff',
      fontSize: '0.875rem',
    };
    const _label = {
      fontSize: '0.75rem',
      color: isDarkMode ? '#9CA3AF' : '#6B7280',
      display: 'block',
      marginBottom: '0.25rem',
    };

    const settings = probeDepthSettings || {};
    const weights  = settings.weights || { noun_phrase: 1.5, domain: 6.0, units: 8.0, generic: 4.0 };

    return (
      <div style={{
        marginTop: '1rem', padding: '1rem',
        backgroundColor: isDarkMode ? '#1c1d1fff' : '#F3F4F6',
        borderRadius: '0.5rem',
      }}>
        <div style={{
          fontSize: '0.875rem', fontWeight: 500,
          color: isDarkMode ? '#aaaaaaff' : '#1F2937',
          marginBottom: '0.75rem',
        }}>
          Probe-Depth Settings:
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
          <div style={{ flex: '1 1 110px' }}>
            <label style={_label}>Min Words:</label>
            <input
              type="number" min="1" max="100"
              value={settings.minWords ?? 15}
              onChange={(e) => {
                e.stopPropagation();
                const v = parseInt(e.target.value);
                setProbeDepthSettings(prev => ({
                  ...(prev || {}),
                  minWords: isNaN(v) ? 15 : v,
                }));
              }}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              style={_input}
            />
          </div>
          <div style={{ flex: '1 1 110px' }}>
            <label style={_label}>Score Threshold:</label>
            <input
              type="number" min="0" max="100"
              value={settings.scoreThreshold ?? 35}
              onChange={(e) => {
                e.stopPropagation();
                const v = parseInt(e.target.value);
                setProbeDepthSettings(prev => ({
                  ...(prev || {}),
                  scoreThreshold: isNaN(v) ? 35 : v,
                }));
              }}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              style={_input}
            />
          </div>
          <div style={{ flex: '1 1 110px' }}>
            <label style={_label}>Severity:</label>
            <select
              value={settings.severity ?? 'medium'}
              onChange={(e) => {
                e.stopPropagation();
                setProbeDepthSettings(prev => ({ ...(prev || {}), severity: e.target.value }));
              }}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              style={{ ..._input, width: '100%' }}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={_label}>Composite weights (advanced — leave defaults unless tuning):</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {['noun_phrase', 'domain', 'units', 'generic'].map((k) => (
              <div key={k} style={{ flex: '1 1 80px' }}>
                <div style={{ ..._label, fontSize: '0.7rem', textTransform: 'capitalize' }}>
                  {k.replace('_', ' ')}{k === 'generic' ? ' (penalty)' : ''}
                </div>
                <input
                  type="number" step="0.5" min="0" max="20"
                  value={weights[k] ?? 0}
                  onChange={(e) => {
                    e.stopPropagation();
                    const v = parseFloat(e.target.value);
                    setProbeDepthSettings(prev => ({
                      ...(prev || {}),
                      weights: { ...(prev?.weights || weights), [k]: isNaN(v) ? 0 : v },
                    }));
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  style={{ ..._input, width: '100%' }}
                />
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: '8px' }}>
          <label style={_label}>Extra domain tokens (comma-separated; appended to backend defaults):</label>
          <input
            type="text"
            placeholder="e.g. injection moulding, hydraulic press, 5-axis"
            value={(settings.domainTokensExtra || []).join(', ')}
            onChange={(e) => {
              e.stopPropagation();
              const list = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
              setProbeDepthSettings(prev => ({ ...(prev || {}), domainTokensExtra: list }));
            }}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            style={{ ..._input, width: '100%' }}
          />
        </div>

        <div style={{
          fontSize: '0.75rem', color: isDarkMode ? '#9CA3AF' : '#6B7280', marginTop: '0.5rem',
        }}>
          Responses scoring below the threshold get flagged. Per-enumerator
          medians and rankings appear in the dashboard's "By Enumerator" tab.
          Backend ships with ~330 default domain words for manufacturing
          contexts; extras are appended.
        </div>
      </div>
    );
  };

  // Render the variable selection div
  const renderVariableSelection = (checkId, isSelected) => {
    
    if (!isSelected) return null;

    const filteredVariables = getFilteredVariables(checkId);
    const check = DATA_QUALITY_CHECKS.find(c => c.id === checkId);
    const selectedVars = selectedVariables[checkId] || [];

    if (filteredVariables.length === 0) {
      return (
        <div className="mt-4 text-sm text-gray-500 italic">
          No variables of the required type are available for this check.
        </div>
      );
    }

    // Gated fields are rule-driven — each rule names its own follow-up + gate columns.
    // Skip the variable picker entirely.
    if (checkId === 'gated_fields') {
      return renderGatedFieldsSettings(checkId);
    }


    if (checkId === 'geolocation') {

      const coordVars = getCoordinateVariables();
      
      return (
        <div style={{ marginTop: '1rem' }}>
          <div style={{
            padding: '1rem',
            backgroundColor: isDarkMode ? '#1c1d1fff' : '#F3F4F6',
            borderRadius: '0.5rem',
            border: isDarkMode ? '1px solid #1c1d1dff' : '1px solid #dbdad1ff',
            
          }}>
            <h5 style={{
              fontSize: '0.8rem',
              fontWeight: '600',
              marginBottom: '0.75rem',
              color: isDarkMode ? '#aaaaaaff' : '#374151'
            }}>
              Select Coordinate Variables (2 required):
            </h5>
            
            {/* Latitude Selection */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                fontSize: '0.75rem',
                color: isDarkMode ? '#9CA3AF' : '#6B7280',
                display: 'block',
                marginBottom: '0.25rem'
              }}>
                Latitude Variable:
              </label>
              <select
                value={selectedVars[0] || ''}
                onChange={(e) => {
                  e.stopPropagation();
                  const newVars = [e.target.value, selectedVars[1] || ''].filter(Boolean);
                  setSelectedVariables(prev => ({
                    ...prev,
                    [checkId]: newVars
                  }));
                }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '0.25rem',
                  border: isDarkMode ? '1px solid #313233' : '1px solid #D1D5DB',
                  backgroundColor: isDarkMode ? '#313233ff' : '#FFFFFF',
                  color: isDarkMode ? '#aaaaaaff' : '#353638ff',
                }}
              >
                <option value="">Select Latitude</option>
                {coordVars.map(variable => (
                  <option key={variable} value={variable}>
                    {variable} ({columnTypes[variable]})
                  </option>
                ))}
              </select>
            </div>
            
            {/* Longitude Selection */}
            <div>
              <label style={{
                fontSize: '0.75rem',
                color: isDarkMode ? '#9CA3AF' : '#535861ff',
                display: 'block',
                marginBottom: '0.25rem'
              }}>
                Longitude Variable:
              </label>
              <select
                value={selectedVars[1] || ''}
                onChange={(e) => {
                  e.stopPropagation();
                  const newVars = [selectedVars[0] || '', e.target.value].filter(Boolean);
                  setSelectedVariables(prev => ({
                    ...prev,
                    [checkId]: newVars
                  }));
                }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '0.25rem',
                  border: isDarkMode ? '1px solid #313233' : '1px solid #D1D5DB',
                  backgroundColor: isDarkMode ? '#313233ff' : '#FFFFFF',
                  color: isDarkMode ? '#aaaaaaff' : '#353638ff',
                }}
              >
                <option value="">Select Longitude</option>
                {coordVars.map(variable => (
                  <option key={variable} value={variable}>
                    {variable} ({columnTypes[variable]})
                  </option>
                ))}
              </select>
            </div>
            
            {selectedVars.length === 2 && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.5rem',
                backgroundColor: isDarkMode ? '#222327ff' : '#eeeeeeff',
                borderRadius: '0.25rem',
                fontSize: '0.75rem',
                color: isDarkMode ? '#575c63ff' : '#0369A1'
              }}>
                ✓ Coordinates configured: {selectedVars[0]} (lat), {selectedVars[1]} (lon)
              </div>
            )}

            {/* Cluster boundaries (optional): the geolocation check gains
                point-in-polygon verdicts — including "misassigned": a point
                inside a DIFFERENT cluster's polygon. Centroid method still
                runs when no file is loaded. */}
            {projectId && (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  marginTop: '0.9rem', paddingTop: '0.9rem',
                  borderTop: isDarkMode ? '1px solid #26272b' : '1px solid #e2e1d9',
                }}
              >
                <label style={{ fontSize: '0.75rem', color: isDarkMode ? '#9CA3AF' : '#6B7280', display: 'block', marginBottom: '0.35rem' }}>
                  Cluster boundaries (optional) — GeoJSON or zipped Shapefile of your lowest cluster level
                </label>
                {boundaryStatus?.loaded ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
                    padding: '0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem',
                    backgroundColor: isDarkMode ? '#12241aff' : '#ECFDF5',
                    color: isDarkMode ? '#34D399' : '#065F46',
                  }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      ✓ {boundaryStatus.groups_count} boundaries loaded (matched on "{boundaryStatus.group_attr}"{boundaryStatus.source ? ` · ${boundaryStatus.source}` : ''})
                    </span>
                    <button type="button" onClick={removeBoundaries} disabled={boundaryBusy} style={{
                      border: 'none', borderRadius: '0.25rem', padding: '4px 10px', cursor: 'pointer',
                      background: isDarkMode ? 'rgba(239,68,68,0.15)' : '#FEE2E2',
                      color: isDarkMode ? '#F87171' : '#B91C1C', fontSize: '0.72rem', fontWeight: 600,
                    }}>Remove</button>
                  </div>
                ) : boundaryInspect ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <select value={boundaryAttr} onChange={(e) => setBoundaryAttr(e.target.value)} style={{
                      flex: 1, minWidth: '160px', padding: '0.45rem', borderRadius: '0.25rem',
                      border: isDarkMode ? '1px solid #313233' : '1px solid #D1D5DB',
                      backgroundColor: isDarkMode ? '#313233ff' : '#FFFFFF',
                      color: isDarkMode ? '#aaaaaaff' : '#353638ff', fontSize: '0.75rem',
                    }}>
                      {(boundaryInspect.attributes || []).map(a => (
                        <option key={a} value={a}>
                          {a}{boundaryInspect.sample_values?.[a]?.length ? ` — e.g. ${boundaryInspect.sample_values[a].slice(0, 2).join(', ')}` : ''}
                        </option>
                      ))}
                    </select>
                    <button type="button" disabled={boundaryBusy || !boundaryAttr}
                      onClick={() => submitBoundaryFile(boundaryFile, boundaryAttr)}
                      style={{
                        border: 'none', borderRadius: '0.25rem', padding: '0.45rem 0.9rem', cursor: 'pointer',
                        background: '#2563EB', color: '#fff', fontSize: '0.75rem', fontWeight: 600,
                        opacity: boundaryBusy ? 0.6 : 1,
                      }}>
                      {boundaryBusy ? 'Saving…' : 'Use this attribute'}
                    </button>
                    <div style={{ width: '100%', fontSize: '0.7rem', color: isDarkMode ? '#6B7280' : '#6B7280' }}>
                      Pick the attribute whose values match your "Group By" column ({boundaryInspect.feature_count} polygons found).
                    </div>
                  </div>
                ) : (
                  <input
                    type="file"
                    accept=".geojson,.json,.zip"
                    disabled={boundaryBusy}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      setBoundaryFile(f);
                      submitBoundaryFile(f, null);
                      e.target.value = '';
                    }}
                    style={{ fontSize: '0.75rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}
                  />
                )}
                {boundaryError && (
                  <div style={{ marginTop: '0.4rem', fontSize: '0.72rem', color: '#EF4444' }}>{boundaryError}</div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }
    
    return (
      <div>
        {/* Search bar + count badge */}
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginTop: '0.5rem',
            marginBottom: '8px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            borderRadius: '8px',
            backgroundColor: isDarkMode ? '#1a1a1e' : '#f0f0f0',
            border: isDarkMode ? '1px solid #2a2a2e' : '1px solid #e0e0e0',
          }}>
            <Search size={14} style={{ color: isDarkMode ? '#666' : '#999', flexShrink: 0 }} />
            <input
              type="text"
              placeholder="Filter variables..."
              value={variableSearch[checkId] || ''}
              onChange={(e) => setVariableSearch(prev => ({ ...prev, [checkId]: e.target.value }))}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                backgroundColor: 'transparent',
                fontSize: '12px',
                color: isDarkMode ? '#ccc' : '#333',
              }}
            />
          </div>
          {selectedVars.length > 0 && (
            <div style={{
              padding: '4px 10px',
              borderRadius: '12px',
              backgroundColor: isDarkMode ? '#7c5b40' : '#e0a85f',
              color: '#fff',
              fontSize: '11px',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}>
              {selectedVars.length} selected
            </div>
          )}
        </div>
        <div 
          className='variableSelectionTab'
          style={{
            border: isDarkMode ? '1px solid #1F1F24ff' : '0px solid #dfdfdf',
            borderRadius: '4px',
            minHeight: '400px',
            maxHeight: '400px',
            overflowY: 'auto',
            backgroundColor: 'inherit',
            marginTop: '0rem'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {filteredVariables
            .filter(v => !variableSearch[checkId] || v.toLowerCase().includes((variableSearch[checkId] || '').toLowerCase()))
            .map((variable) => (
            <div
              key={variable}
              onMouseEnter={() => setHoveredVariable({ checkId, variable })}
              onMouseLeave={() => setHoveredVariable({ checkId: null, variable: null })}
              style={{
                padding: '0.5rem',
                borderTop: isDarkMode ? '1px solid #1F1F24ff' : '0px solid #dfdfdf',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                userSelect: 'none',
                backgroundColor: hoveredVariable.checkId === checkId && hoveredVariable.variable === variable
                  ? (isDarkMode ? '#2a2a2aff' : '#f5f5f5ff') 
                  : 'transparent',
                transform: hoveredVariable.checkId === checkId && hoveredVariable.variable === variable
                  ? 'scale(1.01)'
                  : 'scale(1)',
                transition: 'background-color 0.3s ease, transform 0.3s ease'

              }}
              onClick={() => {
                const currentSelected = selectedVariables[checkId] || [];
                const isSelected = currentSelected.includes(variable);
                let newSelected;
                
                // Handle single vs multiple selection
                if (check.allowMultiple) {
                  newSelected = isSelected
                    ? currentSelected.filter(v => v !== variable)
                    : [...currentSelected, variable];
                } else {
                  newSelected = [variable];
                }
                
                // Enforce maxVariables constraint
                if (check.maxVariables && newSelected.length > check.maxVariables) {
                  alert(`Maximum ${check.maxVariables} variables allowed for ${check.name} check`);
                  return;
                }
                
                // Update selected variables state
                setSelectedVariables(prev => ({
                  ...prev,
                  [checkId]: newSelected
                }));

                // Update variable order for survey flow
                if (checkId === 'survey_flow') {
                  setVariableOrder(prev => ({
                    ...prev,
                    [checkId]: newSelected
                  }));
                }
                
                // Clear previous results when changing selection
                setCheckResults(prev => {
                  const newResults = { ...prev };
                  delete newResults[checkId];
                  return newResults;
                });
              }}
              
            >
              {/*custom checkbox*/}
              <div
                style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '4px',
                  border: isDarkMode ? '0px solid #4B5563' : '0px solid #D1D5DB',
                  backgroundColor: (selectedVariables[checkId] || []).includes(variable)
                    ? (isDarkMode ? 'rgb(10, 197, 221)' : 'rgb(10, 197, 221)') //? (isDarkMode ? '#7c5b40ff' : '#e0a85fff')
                    : (isDarkMode ? '#5e5e5eff' : '#FFFFFF'),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {(selectedVariables[checkId] || []).includes(variable) && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M10 3L4.5 8.5L2 6"
                      stroke="black"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <span>{variable}</span>
              <span className="text-sm text-gray-500">
                ({columnTypes[variable]})
              </span>
            </div>
          ))}
          {filteredVariables.filter(v => !variableSearch[checkId] || v.toLowerCase().includes((variableSearch[checkId] || '').toLowerCase())).length === 0 && variableSearch[checkId] && (
            <div style={{
              padding: '24px',
              textAlign: 'center',
              color: isDarkMode ? '#666' : '#999',
              fontSize: '13px',
            }}>
              No variables matching "{variableSearch[checkId]}"
            </div>
          )}
        </div>
        
        {/* Duration configuration display */}
        {checkId === 'duration' && selectedVars.length > 0 && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            backgroundColor: isDarkMode ? '#1c1d1fff' : '#F3F4F6',
            borderRadius: '0.5rem',
            border: isDarkMode ? '1px solid #1c1d1dff' : '1px solid #D1D5DB' // #ef5844ff
          }}>
            {(() => {
              const config = getDurationConfiguration(checkId, selectedVars, columnTypes);
              return (
                <div>
                  <h5 style={{ 
                    fontSize: '0.8rem', 
                    fontWeight: '600', 
                    marginBottom: '0.5rem',
                    color: isDarkMode ? '#aaaaaaff' : '#374151'
                  }}>
                    Duration Configuration:
                  </h5>
                  <p style={{
                    fontSize: '0.8rem',
                    color: config.type === 'invalid' 
                      ? (isDarkMode ? '#FCA5A5' : '#DC2626')
                      : (isDarkMode ? '#9CA3AF' : '#6B7280'),
                    fontStyle: config.type === 'invalid' ? 'italic' : 'normal'
                  }}>
                    {config.description}
                  </p>
                  
                  {config.type === 'start_end_timestamps' && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                      Duration will be calculated in minutes and displayed over time.
                    </div>
                  )}
                  
                  {(config.type === 'duration_with_date' || config.type === 'pre_calculated') && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                      Timeline visualization will show duration patterns over the collection period.
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
        
        {/* Duration Range Input */}
        {checkId === 'duration' && selectedVars.length > 0 && (
          renderDurationRangeInput(checkId, selectedVars)
        )}
        
        {/* Text Clustering Settings */}
        {checkId === 'text_clustering' && selectedVars.length > 0 && (
          renderTextClusteringSettings(checkId, selectedVars)
        )}

        {/* Sentinel Phrase Settings */}
        {checkId === 'sentinel_phrases' && selectedVars.length > 0 && (
          renderSentinelPhraseSettings(checkId, selectedVars)
        )}

        {/* Probe-Depth Settings */}
        {checkId === 'probe_depth' && selectedVars.length > 0 && (
          renderProbeDepthSettings(checkId, selectedVars)
        )}

        {/* Straight-Lining Settings — always shown when selected (vars optional) */}
        {checkId === 'straight_lining' && (
          renderStraightLiningSettings(checkId)
        )}

        {/* Show ordering interface for survey flow */}
        {checkId === 'survey_flow' && selectedVars.length >= 2 && (
          renderSurveyFlowOrdering(checkId, selectedVars)
        )}
      </div>
    );

    

  };

  const styles = {
    
    container: {
      padding: isMobile ? '24px 12px 24px 12px' : '40px 60px 24px 60px',
      backgroundColor: isDarkMode ? '#0D0E0E' : '#F5F5F599',
      width: '100%'
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '600',
      marginBottom: '1.5rem'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(450px, 1fr))', 
      gap: '1rem',
      marginBottom: '2rem'
    },
    card: {
      border: isDarkMode ? '1px solid #292929ff' : '1px solid #dfdfdf',
      //width: '300px',
      borderRadius: '15px',
      padding: '1rem',
      // backgroundColor: 'inherit',
      backgroundColor: isDarkMode ? '#0D0E0E' : '#f5f5f5ff',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: isDarkMode ? '0px 0px 0px #1D1D1D' : '0px 3px 3px 3px #F2F2F2',
      
    },
    selectedCard: {     // selected card
      border: isDarkMode ? '10px solid #1F1F24' : '10px solid #eeeeeeff',  
      background: isDarkMode
        ? 'linear-gradient(135deg, #1F1F24 0%, #1F1F24 50%, #1F1F24 100%)'
        : 'linear-gradient(135deg, #eeeeeeff 0%, #eeeeeeff 50%, #ebebebff 100%)',
      boxShadow: isDarkMode ? '0px 0px 0px 0px #000000ff' : '5px 5px 5px 5px #ffffffff',
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      marginBottom: '0.5rem',
    },
    cardTitle: {
      fontWeight: '500',
      fontSize: '1.1rem'
    },
    cardDescription: {
      fontSize: '0.8rem',
      color: 'inherit',
      marginBottom: '1rem'
    },
    runButton: {
      backgroundColor: isDarkMode ?  'rgb(10, 197, 221)' : 'rgb(10, 197, 221)',
      color: isDarkMode ? '#000000' : '#000000',
      padding: '0.75rem 1.5rem',
      borderRadius: '40px',
      border: 'none',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'background-color 0.2s ease',
      justifyContent: 'center',
      alignItems: 'center',
      display: 'flex',
      margin: '0 auto',
      marginBottom: '50px',
      fontSize: '16px'
    },
    disabledButton: {
      backgroundColor: '#585858ff',
      cursor: 'not-allowed'
    },
    loading: {
      color: '#4a5568',
      marginLeft: '1rem',
      fontSize: '0.8rem'
    },
  };

  return (  
    
  <>

    {/* Loading Overlay - Full Screen */}
    <AILoadingOverlay 
     isVisible={isLoadingChecks} 
     isDarkMode={isDarkMode}
     title="Running quality checks"
     subtitle="Only the detectors you selected are running"
     tasks={selectedChecks.map(id => {
       const c = DATA_QUALITY_CHECKS.find(x => x.id === id);
       const nVars = (selectedVariables[id] || []).length;
       return {
         id,
         label: c?.name || id,
         detail: nVars > 0
           ? `${nVars} variable${nVars === 1 ? '' : 's'}`
           : id === 'gated_fields' ? 'configured rules'
           : id === 'straight_lining' ? 'auto-detect'
           : null,
       };
     })}
     realTimeStats={{
       totalRows: data?.total_rows || data?.full_data?.length || data?.data?.length || 0,
       columnsAnalyzed: columnTypes 
         ? Object.keys(columnTypes).length 
         : (data?.total_variables || data?.columns?.length || (data?.data?.[0] ? Object.keys(data.data[0]).length : 0)),
     }}
     showDetailedLogs={true}
   />

    {/*main edits*/}
    {/* Data Quality Checks Section */}
    <div 
      className='ConfigureDetectorsContainer'
      style={styles.container}>
      <div style={{
        display: 'flex',
        width: '90%',
        alignItems: 'center',
        justifyContent: 'center',
        // marginBottom: '20px', // ola man
        transition: '1s ease-in-out',
        marginLeft:'0vw'
      }}>
        <h2 style={{fontSize: '25px', fontWeight: '500', display: 'flex', paddingRight: '20px'}}>Select Quality Detectives</h2>

        <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
          <button
            type="button"
            onClick={selectAllChecks}
            disabled={selectedChecks.length === DATA_QUALITY_CHECKS.length}
            style={{
              padding: '6px 16px',
              borderRadius: '30px',
              border: `1px solid ${isDarkMode ? '#3a3a3a' : '#d0d0d0'}`,
              backgroundColor: 'transparent',
              color: isDarkMode ? '#E0E0E0' : '#000000',
              fontSize: '13px',
              fontWeight: '500',
              cursor: selectedChecks.length === DATA_QUALITY_CHECKS.length ? 'not-allowed' : 'pointer',
              opacity: selectedChecks.length === DATA_QUALITY_CHECKS.length ? 0.5 : 1,
              transition: 'all 0.2s ease',
            }}
          >
            Select All
          </button>
          <button
            type="button"
            onClick={deselectAllChecks}
            disabled={selectedChecks.length === 0}
            style={{
              padding: '6px 16px',
              borderRadius: '30px',
              border: `1px solid ${isDarkMode ? '#3a3a3a' : '#d0d0d0'}`,
              backgroundColor: 'transparent',
              color: isDarkMode ? '#E0E0E0' : '#000000',
              fontSize: '13px',
              fontWeight: '500',
              cursor: selectedChecks.length === 0 ? 'not-allowed' : 'pointer',
              opacity: selectedChecks.length === 0 ? 0.5 : 1,
              transition: 'all 0.2s ease',
            }}
          >
            Deselect All
          </button>
        </div>
        {/*
        <button
          onClick={() => setIsExpandedDQC(!isExpandedDQC)}
          style={{
            border: 'none',
            borderRadius: '50%',
            backgroundColor: isExpandedDQC ? (isDarkMode ? '#474747ff' : '#c7c7c7ff') : (isDarkMode ? '#474747ff' : '#c7c7c7ff'),
            transition: 'maxHeight 0.3s ease',
            rotate: isExpandedDQC ? '45deg' : '0deg',
            fontSize: '20px',
            cursor: 'pointer',
            color: isExpandedDQC ? (isDarkMode ? '#000000ff' : '#000000ff') : (isDarkMode ? '#000000ff' : '#000000ff'),
            width: '35px',
            height: '35px',
            minWidth: '35px',
            minHeight: '35px',
            flexShrink: 0,
            display: 'flex',
            textAlign: 'center',
            alignItems: 'center',
            justifyContent: 'center',
            justifyItems: 'center',
            marginLeft: '0px',
            marginTop: '0px',
            userSelect: 'none'
          }}
        >
          +
        </button>
        */}
      </div>


      
      {onBackToModeSelector && (
        <button
          onClick={onBackToModeSelector}
          style={{
            padding: '8px 16px',
            backgroundColor: 'transparent',
            border: '1px solid #6B7280',
            borderRadius: '6px',
            color: isDarkMode ? '#9CA3AF' : '#6B7280',
            cursor: 'pointer',
            marginBottom: '16px'
          }}
        >
          ← Back
        </button>
      )}
            
      <div style={{
        maxHeight: isExpandedDQC ? '1050vh' : '0px',
        transition: '0.3s ease-in-out',
        overflow: 'hidden',
        borderTop: isExpandedDQC ? '0px solid #dfdfdf' : (isDarkMode ? '1px solid #5c5c5c' : '1px solid #dfdfdfff'),
        borderBottom: isExpandedDQC ? (isDarkMode ? '0px solid #5c5c5c' : '0px solid #dfdfdf') : '0px solid #dfdfdf',
      }}>
        {renderError()}
        {renderTypeValidationWarnings()}
        {checkError && <div style={styles.error}>{checkError}</div>}
        {renderVariableOptions()}

        <div style={styles.grid}>
          {DATA_QUALITY_CHECKS.map((check) => {
            const Icon = check.icon;
            const isSelected = selectedChecks.includes(check.id);
            
            return (
              <div 
                key={check.id}
                style={{
                  
                  ...styles.card,
                  ...(isSelected ? styles.selectedCard : {})
                }}
                onClick={() => toggleCheck(check.id)}
              >
                <div style={{
                  ...styles.cardHeader,
                  justifyContent: 'space-between'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Icon 
                      size={24} 
                      color={isSelected ? (isDarkMode ? '#4a5568' : '#000000ff') : '#474c55ff'} 
                    />
                    <h3 style={styles.cardTitle}>{check.name}</h3>
                    {isSelected && (selectedVariables[check.id] || []).length > 0 && (
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '10px',
                        backgroundColor: isDarkMode ? '#7c5b40' : '#e0a85f',
                        color: '#fff',
                        fontSize: '11px',
                        fontWeight: '600',
                        lineHeight: '1.4'
                      }}>
                        {(selectedVariables[check.id] || []).length}
                      </span>
                    )}
                  </div>
                  
                  {/* Select All / Clear Selection buttons - only show when card is selected and not geolocation */}
                  {isSelected && check.id !== 'geolocation' && (
                    <div 
                      style={{ 
                        display: 'flex', 
                        gap: '8px',
                        marginLeft: 'auto'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={(e) => selectAllVariables(check.id, e)}
                        style={{
                          padding: '4px 10px',
                          fontSize: '11px',
                          fontWeight: '500',
                          borderRadius: '6px',
                          border: 'none',
                          backgroundColor: isDarkMode ? '#3d3d3dff' : '#e5e5e5ff',
                          color: isDarkMode ? '#a0a0a0ff' : '#555555ff',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = isDarkMode ? '#4d4d4dff' : '#d5d5d5ff';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = isDarkMode ? '#3d3d3dff' : '#e5e5e5ff';
                        }}
                      >
                        Select All
                      </button>
                      <button
                        onClick={(e) => clearVariableSelection(check.id, e)}
                        style={{
                          padding: '4px 10px',
                          fontSize: '11px',
                          fontWeight: '500',
                          borderRadius: '6px',
                          border: 'none',
                          backgroundColor: isDarkMode ? '#3d3d3dff' : '#e5e5e5ff',
                          color: isDarkMode ? '#a0a0a0ff' : '#555555ff',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = isDarkMode ? '#4d4d4dff' : '#d5d5d5ff';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = isDarkMode ? '#3d3d3dff' : '#e5e5e5ff';
                        }}
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>
                <p style={styles.cardDescription}>{check.description}</p>
                {renderVariableSelection(check.id, isSelected)}
              </div>
            );
          })}
        </div>

        {selectedChecks.length > 0 && (
          <div style={{ 
            textAlign: 'center',
            maxHeight: isExpandedDQC ? '500px' : '0px',
            transition: '0.3s ease-in-out'
          }}>
            <button
              onClick={runChecks}
              disabled={selectedChecks.length === 0 || isLoadingChecks}
              style={{
                ...styles.runButton,
                ...(isLoadingChecks ? styles.disabledButton : {})
              }}
            >
              {isLoadingChecks ? 'Running Checks...' : 'Run Selected Checks'}
            </button>
          </div>
        )}
      </div>
    </div>
      

  </>
);
};

export default ConfigureDetectors;